import {useSpaceAvailability} from '../../../hooks/useWebSocket';
// NuevaCitaContent.tsx — Co Marca — Lógica de Citas (recibe sesión, POS y sucursal)
import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, generatePath } from "react-router-dom";
import { ROUTES } from "../../../routes/routePaths";
import {
  Calendar,
  Clock,
  MapPin,
  Package,
  CreditCard,
  ArrowLeft,
  CheckCircle,
  X,
  Search,
  Plus,
  Eye,
  Users,
  ChevronRight,
  UserPlus,
  DollarSign,
  Percent,
  SquarePen,
  Zap,
  Timer,
  LayoutGrid,
  RefreshCw,
  Gift,
} from "lucide-react";
import { DateRange } from "react-day-picker";
import { useMonthlySpaceOccupancy } from "../../../components/booking/useMonthlySpaceOccupancy";
import { bookingCalendarTokens } from "../../../components/booking/bookingCalendarTokens";
import "react-day-picker/dist/style.css";
import Modal from "../../../components/Modal/Modal";
import ClientePOSModal from "../../../components/posCita/ClientePOSModal";
import ModalAgregarClientesAsociados from "../../../components/posCita/ModalAgregarClientesAsociados";
import {
  evaluarPlanReserva,
  Cita,
  fetchCategories,
  fetchServiciosByCategory,
  fetchPrecioPlanesServicios,
  fetchEspacioServicios,
  fetchPlanDuracionConfigs,
  PlanDuracionConfig,
  Servicio,
  PrecioPlanServicio,
  EspacioServicio,
  createCita,
  fetchCitas,
  fetchClientes,
  Cliente,
  fetchCupones,
  Cupon,
  fetchCuponesUsados,
} from "../../../services/citasService";
import {
  fetchAfiliacionByCliente,
  Afiliacion,
} from "../../../services/afiliadosService";
import ProductosReservaSelector, { ProductoSeleccionado } from "../../../components/posCita/ProductosReservaSelector";
import { SesionActivaResponse } from "../../../services/pos/sesionPOSService";
import { PuntoDeVenta } from "../../../services/pos/puntoDeVentaService";
import { DayPicker } from "react-day-picker";
import {
  format,
  addDays,
  addHours,
  differenceInMinutes,
  parse,
  startOfDay,
} from "date-fns";
import { es } from "date-fns/locale";
import { fetchEspaciosDisponiblesPorServicio } from "../../../services/espacioServiciosService";
import CustomerGiftHoursPanel from "./CustomerGiftHoursPanel";

// ── Paleta de diseño ────────────────────────────────────────
// Paleta CoMarca + tokens de precisión (navy profundo, superficies blancas,
// sombras azuladas suaves, radios moderados, números tabulares).
const C = {
  primary: "#FF2052",
  primaryDark: "#9B004A",
  secondary: "#007FC8",
  secondaryDark: "#004d7a",
  secondaryLight: "#00DBB9",
  accent: "#FFD97A",
  accentDark: "#EC8400",
  navy: "#0A1F44",
  ink: "#0f172a",
  muted: "#64748b",
  line: "#e2e8f0",
  canvas: "#f6f9fc",
  shadowSm: "0 1px 2px rgba(10,31,68,.06), 0 2px 8px rgba(10,31,68,.05)",
  shadowMd: "0 4px 16px rgba(10,31,68,.08)",
};

const css = `
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700;800;900&display=swap');
.ncp * { font-family:'DM Sans',sans-serif !important; box-sizing:border-box; }
@keyframes ncpSpin { to{transform:rotate(360deg);} }
@keyframes ncpFadeUp { from{opacity:0;transform:translateY(12px);}to{opacity:1;transform:translateY(0);} }
@keyframes ncpPulse { 0%,100%{opacity:1;}50%{opacity:.5;} }
.ncp-num { font-variant-numeric:tabular-nums; }

.ncp-scroll::-webkit-scrollbar{width:5px;height:5px;}
.ncp-scroll::-webkit-scrollbar-track{background:transparent;}
.ncp-scroll::-webkit-scrollbar-thumb{background:rgba(0,127,200,.25);border-radius:10px;}
.ncp-scroll::-webkit-scrollbar-thumb:hover{background:rgba(0,127,200,.45);}

/* Barra de filtros (persistente) */
.ncp-filter-bar { display:flex; flex-wrap:wrap; gap:6px; align-items:center; }
.ncp-chip {
    min-height:36px; padding:6px 12px; border-radius:9px; border:1px solid #d5dfea; background:#fff;
    color:#334155; font-size:.76rem; font-weight:700; cursor:pointer; display:inline-flex; align-items:center; gap:6px;
    transition:all .15s ease; line-height:1.1; max-width:100%;
}
.ncp-chip:hover { border-color:${C.secondary}; color:${C.secondaryDark}; background:#f4f9fd; }
.ncp-chip[aria-pressed="true"] { background:#e8f4fb; border-color:#8ecae9; color:${C.secondaryDark}; box-shadow:0 2px 8px rgba(0,127,200,.10); }
.ncp-chip:focus-visible { outline:3px solid #fbbf24; outline-offset:2px; }

/* Secciones del flujo (apiladas, a simple vista) */
.ncp-section { border:1px solid ${C.line}; border-radius:12px; background:#fff; padding:14px; margin-top:12px; box-shadow:${C.shadowSm}; transition:border-color .2s, box-shadow .2s; }
.ncp-section.current { border-color:#b9dced; box-shadow:0 0 0 3px rgba(142,202,233,.16), ${C.shadowMd}; }
.ncp-section-head { display:flex; align-items:center; justify-content:space-between; gap:10px; margin-bottom:10px; flex-wrap:wrap; }
.ncp-section-title { display:flex; align-items:center; gap:8px; font-size:.86rem; font-weight:800; color:${C.navy}; margin:0; letter-spacing:-.01em; }
.ncp-section-sub { font-size:.74rem; font-weight:600; color:${C.muted}; }

/* Tarjetas visuales de servicio: la imagen y el nombre son protagonistas. */
.ncp-service-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(210px,1fr)); gap:12px; }
.ncp-service {
    text-align:left; border-radius:14px; border:1px solid ${C.line}; background:#fff; padding:0; cursor:pointer;
    display:flex; flex-direction:column; min-height:218px; width:100%; transition:all .18s ease; box-shadow:${C.shadowSm};
    color:${C.ink}; position:relative; overflow:hidden;
}
.ncp-service:hover { border-color:${C.secondary}80; box-shadow:0 4px 14px rgba(0,127,200,.14); transform:translateY(-1px); }
.ncp-service[aria-pressed="true"] { border-color:#8ecae9; background:#f4faff; box-shadow:inset 0 0 0 1px #b9dced, ${C.shadowSm}; }
.ncp-service:focus-visible { outline:3px solid #fbbf24; outline-offset:2px; }
.ncp-service-image { width:100%; height:132px; object-fit:cover; display:block; background:#e8eef7; }
.ncp-service-image-placeholder { width:100%; height:132px; display:flex; align-items:center; justify-content:center; background:linear-gradient(135deg,#e8f3fb,#eef2f7); }
.ncp-service-body { width:100%; min-width:0; flex:1; display:block; padding:13px 14px 14px; position:relative; }
.ncp-service-name { font-size:1rem; font-weight:900; color:${C.ink}; margin:0; line-height:1.2; letter-spacing:-.015em; padding-right:20px; }
.ncp-service-desc { font-size:.7rem; color:${C.muted}; margin:3px 0 0; display:-webkit-box; -webkit-line-clamp:2; -webkit-box-orient:vertical; overflow:hidden; line-height:1.3; }
.ncp-service-meta { font-size:.64rem; color:#94a3b8; font-weight:700; margin:0 0 2px; text-transform:uppercase; letter-spacing:.05em; }
.ncp-tag { display:inline-flex; align-items:center; gap:3px; margin-top:6px; padding:2px 7px; border-radius:6px; font-size:.62rem; font-weight:800; background:${C.accentDark}14; color:#9a4a00; white-space:nowrap; }

.ncp-sel-card {
    border-radius:12px; border:1.5px solid ${C.line}; background:#fff;
    cursor:pointer; overflow:hidden; transition:all .2s cubic-bezier(.16,1,.3,1);
    position:relative; box-shadow:${C.shadowSm};
}
.ncp-sel-card:hover { border-color:${C.secondary}60; box-shadow:0 8px 24px rgba(0,127,200,.12); transform:translateY(-2px); }
.ncp-sel-card.active { border-color:${C.secondary}; box-shadow:0 8px 24px rgba(0,127,200,.18); }
.ncp-sel-card.unavailable { opacity:.6; cursor:not-allowed; }
.ncp-sel-card:focus-visible,.ncp-plan-card:focus-visible,.ncp-client-card:focus-visible { outline:3px solid #fbbf24; outline-offset:2px; }

.ncp-plan-card {
    border-radius:11px; border:1.5px solid ${C.line}; background:#fff;
    cursor:pointer; padding:12px 14px; transition:all .2s cubic-bezier(.16,1,.3,1); box-shadow:${C.shadowSm};
}
.ncp-plan-card:hover { border-color:${C.secondary}60; box-shadow:0 6px 20px rgba(0,127,200,.1); transform:translateY(-1px); }
.ncp-plan-card.active { border-color:#8ecae9; background:#f4faff; box-shadow:inset 0 0 0 1px #b9dced, ${C.shadowSm}; }

.ncp-tab { flex:1; padding:12px 8px; font-size:.8rem; font-weight:700; border:none; cursor:pointer; transition:all .2s; display:flex;align-items:center;justify-content:center;gap:6px; }
.ncp-tab.active { background:#fff; color:${C.secondary}; box-shadow:inset 0 -2px 0 ${C.secondary}; }
.ncp-tab.inactive { background:#fafafa; color:#94a3b8; }
.ncp-tab.inactive:hover { background:#f1f5f9; color:#64748b; }
.ncp-day-occupied:not(.rdp-selected) .rdp-day_button {
    background:${bookingCalendarTokens.occupiedBackground} !important;color:${bookingCalendarTokens.occupiedText} !important;
    box-shadow:inset 0 0 0 1.5px ${bookingCalendarTokens.occupiedBorder} !important;font-weight:800 !important;position:relative;
}
.ncp-day-occupied:not(.rdp-selected) .rdp-day_button::after {
    content:'';position:absolute;bottom:3px;left:50%;transform:translateX(-50%);
    width:5px;height:5px;border-radius:50%;background:${bookingCalendarTokens.occupiedDot};
}
.ncp-day-occupied.rdp-selected .rdp-day_button {
    box-shadow:inset 0 0 0 1.5px ${bookingCalendarTokens.occupiedBorder},0 3px 10px ${bookingCalendarTokens.selectedShadow} !important;
    position:relative;
}
.ncp-day-occupied.rdp-selected .rdp-day_button::after {
    content:'';position:absolute;bottom:3px;left:50%;transform:translateX(-50%);
    width:6px;height:6px;border-radius:50%;background:${bookingCalendarTokens.occupiedDot};border:1px solid #fff;
}
.ncp .rdp-root { margin:0;width:100%; }
.ncp .rdp-month_grid { width:100%;max-width:none; }
.ncp .rdp-month_caption { margin-bottom:10px;justify-content:center;font-weight:900;color:#0f172a; }
.ncp .rdp-weekday { color:#94a3b8;font-size:.62rem;font-weight:800;letter-spacing:.06em; }
.ncp .rdp-day { padding:2px; }
.ncp .rdp-day_button { border-radius:${bookingCalendarTokens.radius} !important;transition:all .12s; }

.ncp-client-card {
    border-radius:11px; border:1.5px solid ${C.line};
    padding:12px 14px; cursor:pointer; transition:all .2s cubic-bezier(.16,1,.3,1);
    background:#fff;
}
.ncp-client-card:hover { border-color:${C.secondary}35; background:${C.secondary}05; }
.ncp-client-card.selected { border-color:${C.secondary}; background:${C.secondary}08; }

.ncp-confirm-btn {
    width:100%; padding:13px; border-radius:11px; border:none;
    font-size:.9rem; font-weight:800; display:flex; align-items:center; justify-content:center; gap:8px;
    transition:all .25s cubic-bezier(.16,1,.3,1); cursor:pointer;
}
.ncp-confirm-btn.ready {
    background:linear-gradient(135deg,${C.secondary},${C.secondaryDark});
    color:#fff; box-shadow:0 4px 18px ${C.secondary}35;
}
.ncp-confirm-btn.ready:hover { transform:translateY(-2px); box-shadow:0 8px 28px ${C.secondary}45; }
.ncp-confirm-btn.disabled { background:#f1f5f9; color:#cbd5e1; cursor:not-allowed; }
.ncp-confirm-btn:focus-visible,.ncp-back-btn:focus-visible,.ncp-tab:focus-visible { outline:3px solid #fbbf24; outline-offset:2px; }

.ncp-input { transition:all .2s; }
.ncp-input:focus { outline:3px solid transparent; border-color:${C.secondary} !important; box-shadow:0 0 0 3px ${C.secondary}14; }
.ncp-input:focus-visible { outline:3px solid #fbbf24; outline-offset:1px; }

.ncp .rdp-selected .rdp-day_button,.ncp .rdp-range_start .rdp-day_button,.ncp .rdp-range_end .rdp-day_button {
    background:${bookingCalendarTokens.selected} !important; color:#fff !important; border-radius:${bookingCalendarTokens.radius} !important;
}
.ncp .rdp-range_middle .rdp-day_button { background:${bookingCalendarTokens.selected}20 !important; color:${bookingCalendarTokens.selected} !important; border-radius:0 !important; }
.ncp .rdp-day:not(.rdp-selected) .rdp-day_button:hover { background:${bookingCalendarTokens.selected}12 !important; }
.ncp .rdp-button_previous:hover,.ncp .rdp-button_next:hover { background:${bookingCalendarTokens.selected}12 !important; }
.ncp .rdp-today:not(.rdp-selected) .rdp-day_button { color:${bookingCalendarTokens.selected} !important; font-weight:800 !important; }
.ncp .rdp-disabled { opacity:.3 !important; }
.ncp-main-panel,.ncp-customer-panel{min-width:0;}
/* Size the booking form to its own panel, including three-column POS layouts. */
.ncp,.ncp *{box-sizing:border-box;}
.ncp-main-scroll{container-name:ncp-form;container-type:inline-size;min-width:0;max-width:100%;}
.ncp-date-grid{grid-template-columns:minmax(0,1fr) !important;min-width:0;}
.ncp-date-grid>div,.ncp-date-fields>div{min-width:0;max-width:100%;}
.ncp-date-fields{grid-template-columns:repeat(2,minmax(0,1fr)) !important;min-width:0;}
.ncp-date-fields label{flex-wrap:wrap;overflow-wrap:anywhere;}
.ncp-date-fields .ncp-num{overflow-wrap:anywhere;}
.ncp-date-fields input{min-width:0;max-width:100%;min-height:44px !important;}
.ncp .rdp-root{max-width:100%;min-width:0;}
.ncp .rdp-months{display:block;max-width:100%;min-width:0;}
.ncp .rdp-month{width:100%;max-width:100%;min-width:0;}
.ncp .rdp-month_grid{table-layout:fixed;width:100%;max-width:100%;}
.ncp .rdp-weekday,.ncp .rdp-day{width:calc(100% / 7);min-width:0;}
.ncp .rdp-day_button{max-width:100%;margin-inline:auto;}
@container ncp-form (min-width:760px){
  .ncp-date-grid{grid-template-columns:minmax(0,1.2fr) minmax(0,1fr) !important;}
}
@container ncp-form (max-width:360px){
  .ncp-date-fields{grid-template-columns:minmax(0,1fr) !important;}
  .ncp-section{padding:10px;}
  .ncp .rdp-root{--rdp-day-width:36px;--rdp-day-height:44px;--rdp-day_button-width:36px;--rdp-day_button-height:44px;}
}

.ncp-selection-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(200px,1fr));gap:10px;}
@media(max-width:900px){
  .ncp{height:auto !important;min-height:100%;flex-direction:column;overflow:visible !important;padding:10px;gap:10px;}
  .ncp-main-panel{margin:0 !important;overflow:visible !important;order:1;}
  .ncp-main-scroll{overflow:visible !important;padding:14px !important;}
  .ncp-customer-panel{width:100% !important;margin:0 !important;order:2;max-height:46dvh;min-height:260px;}
  .ncp-date-grid{grid-template-columns:1fr !important;}
}
@media(max-width:560px){
  .ncp{padding:7px;}
  .ncp-main-scroll{padding:12px !important;}
  .ncp-service-grid{grid-template-columns:repeat(2,minmax(0,1fr));}
  .ncp-service-image,.ncp-service-image-placeholder{height:110px;}
  .ncp-selection-grid{grid-template-columns:1fr !important;gap:8px !important;}
  .ncp-date-grid,.ncp-time-grid,.ncp-date-fields{grid-template-columns:1fr !important;}
  .ncp-customer-panel{max-height:52dvh;border-radius:14px !important;}
  .ncp-client-card{padding:12px;}
}
@media(max-width:400px){
  .ncp{padding:4px;}
  .ncp-main-scroll{padding:9px !important;}
  .ncp-section{padding:10px;border-radius:10px;}
  .ncp-service-grid{grid-template-columns:1fr;}
  .ncp-chip{padding:6px 10px;font-size:.72rem;min-height:40px;}
}
`;

// ── Helpers ─────────────────────────────────────────────────
function fmtDateWithoutTZ(date: Date): string {
  const y = date.getFullYear(),
    m = String(date.getMonth() + 1).padStart(2, "0"),
    d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}
function parseLocalDate(value: string): Date {
  return new Date(`${value}T00:00:00`);
}
function fmtDateSpanish(s: string | null): string {
  if (!s) return "—";
  const d = new Date(s + "T00:00:00");
  return new Intl.DateTimeFormat("es-ES", {
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(d);
}
function fmtMonto(m: any): string {
  if (m == null) return "0.00";
  const n = Number(m);
  return isNaN(n) ? "0.00" : n.toFixed(2);
}

// Regla nocturna compartida con el backend (``Cita.update_or_create_sessions``
// y ``space_availability``): si hora_fin <= hora_inicio, la reserva termina al
// DÍA SIGUIENTE. Igualdad = 24 horas.
export const isOvernightRange = (
  horaInicio: string | null,
  horaFin: string | null,
): boolean => !!horaInicio && !!horaFin && horaFin <= horaInicio;

// Horas diarias reservadas usando datetimes completos (nunca dos parse sobre
// la misma fecha que producen duraciones negativas).
export const dailyHours = (
  baseDate: Date,
  horaInicio: string,
  horaFin: string,
): number => {
  const start = parse(horaInicio, "HH:mm", baseDate);
  let end = parse(horaFin, "HH:mm", baseDate);
  if (end <= start) end = addDays(end, 1);
  return differenceInMinutes(end, start) / 60;
};

interface Categoria {
  id: number;
  nombre: string;
  estado: boolean;
  imagen: string;
}
interface ClienteAsociadoInfo {
  id: number;
  userId: number;
  first_name: string;
  last_name: string;
  email: string;
}

const ALL_FILTER = "all" as const;
type CategoryFilter = number | typeof ALL_FILTER;
const EMPTY_CATEGORIES: Categoria[] = [];
const EMPTY_CITAS: Cita[] = [];

const sameCategories = (a: Categoria[], b: Categoria[]) =>
  a.length === b.length &&
  a.every(
    (c, i) =>
      c.id === b[i].id &&
      c.nombre === b[i].nombre &&
      c.imagen === b[i].imagen &&
      c.estado === b[i].estado,
  );

// Estados de carga y error visibles (nunca solo console.error). Cada recurso
// remoto (categorías, servicios, planes, espacios, clientes, citas) los usa.
const LoadingLine: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p
    role="status"
    style={{
      fontSize: ".74rem",
      color: C.muted,
      margin: "0 0 8px",
      display: "flex",
      alignItems: "center",
      gap: "6px",
    }}
  >
    <span
      aria-hidden="true"
      style={{
        width: "12px",
        height: "12px",
        border: `2px solid ${C.secondary}`,
        borderTopColor: "transparent",
        borderRadius: "50%",
        animation: "ncpSpin .7s linear infinite",
        display: "inline-block",
        flexShrink: 0,
      }}
    />{" "}
    {children}
  </p>
);
const ErrorBox: React.FC<{ message: string; onRetry: () => void }> = ({
  message,
  onRetry,
}) => (
  <div
    role="alert"
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: "10px",
      flexWrap: "wrap",
      padding: "10px 12px",
      borderRadius: "10px",
      background: "#fef2f2",
      border: "1px solid #fecaca",
      fontSize: ".74rem",
      fontWeight: "700",
      color: "#b91c1c",
      marginBottom: "8px",
    }}
  >
    <span>{message}</span>
    <button
      type="button"
      onClick={onRetry}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "4px",
        minHeight: "32px",
        padding: "4px 10px",
        borderRadius: "8px",
        border: "1px solid #fecaca",
        background: "#fff",
        color: "#b91c1c",
        fontSize: ".72rem",
        fontWeight: "800",
        cursor: "pointer",
      }}
    >
      <RefreshCw size={12} /> Reintentar
    </button>
  </div>
);
const EmptyNote: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p
    style={{
      fontSize: ".76rem",
      color: C.muted,
      margin: 0,
      padding: "12px",
      borderRadius: "10px",
      background: C.canvas,
      border: `1px dashed ${C.line}`,
    }}
  >
    {children}
  </p>
);

const activateWithKeyboard = (
  event: React.KeyboardEvent<HTMLElement>,
  action: () => void,
) => {
  if (event.target !== event.currentTarget) return;
  if (event.key === "Enter" || event.key === " ") {
    event.preventDefault();
    action();
  }
};

interface NuevaCitaContentProps {
  sesionActiva: SesionActivaResponse;
  puntoDeVenta: PuntoDeVenta;
  sucursalId: number;
  operadorToken?: string | null;
  requireOperatorPin?: boolean;
  espacioInicial?: EspacioServicio | null;
  fechaInicial?: string;
  onReservaCreada?: (
    cita: Cita,
    accion: "guardar" | "despues" | "cobrar",
  ) => void;
}

// Pasos operativos: 1 servicio (con filtros) → 2 plan → 3 espacio (si aplica) → 4 fechas
const STEP_SERVICE = 1,
  STEP_PLAN = 2,
  STEP_SPACE = 3,
  STEP_DATES = 4;

// Frontera estable por AUTORIDAD COMPUESTA: sucursal + sesión POS + punto de
// venta son, juntos, la autoridad de la reserva. Al cambiar cualquiera de las
// tres dimensiones React desmonta el árbol anterior y monta uno nuevo (`key`).
// Desde el primer render de la nueva identidad no existe ningún dato, selección
// ni respuesta en vuelo del contexto anterior; las continuaciones de la
// instancia vieja se descartan por mountedRef/request-id.
const reservationAuthorityKey = (props: NuevaCitaContentProps) =>
  `${props.sucursalId}|${props.sesionActiva?.id ?? ""}|${props.puntoDeVenta?.id ?? ""}|${props.operadorToken ?? ""}`;

const NuevaCitaContent: React.FC<NuevaCitaContentProps> = (props) => (
  <NuevaCitaSucursalContent key={reservationAuthorityKey(props)} {...props} />
);

const NuevaCitaSucursalContent: React.FC<NuevaCitaContentProps> = ({
  sesionActiva,
  puntoDeVenta,
  sucursalId,
  operadorToken,
  requireOperatorPin = false,
  espacioInicial,
  fechaInicial,
  onReservaCreada,
}) => {
  const navigate = useNavigate();
  const [step, setStep] = useState(STEP_SERVICE);
  const [categories, setCategories] = useState<Categoria[]>([]);
  const [categoriesSucursalId, setCategoriesSucursalId] = useState<
    number | null
  >(null);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [categoriesError, setCategoriesError] = useState<string | null>(null);
  const [categoriesReloadKey, setCategoriesReloadKey] = useState(0);
  const [categoryFilter, setCategoryFilter] =
    useState<CategoryFilter>(ALL_FILTER);
  const [planConfigs, setPlanConfigs] = useState<PlanDuracionConfig[]>([]);
  const [planConfigsLoading, setPlanConfigsLoading] = useState(false);
  const [planConfigsError, setPlanConfigsError] = useState<string | null>(null);
  const [planConfigsReloadKey, setPlanConfigsReloadKey] = useState(0);
  const [services, setServices] = useState<Servicio[]>([]);
  const [servicesLoading, setServicesLoading] = useState(false);
  const [servicesError, setServicesError] = useState<string | null>(null);
  const [servicesReloadKey, setServicesReloadKey] = useState(0);
  const [pricePlans, setPricePlans] = useState<PrecioPlanServicio[]>([]);
  const [plansLoading, setPlansLoading] = useState(false);
  const [plansError, setPlansError] = useState<string | null>(null);
  const [plansReloadKey, setPlansReloadKey] = useState(0);
  const [spaces, setSpaces] = useState<EspacioServicio[]>([]);
  const [spacesLoading, setSpacesLoading] = useState(false);
  const [spacesError, setSpacesError] = useState<string | null>(null);
  const [spacesReloadKey, setSpacesReloadKey] = useState(0);
  const [selectedService, setSelectedService] = useState<Servicio | null>(null);
  const [selectedPlan, setSelectedPlan] = useState<PrecioPlanServicio | null>(
    null,
  );
  const [selectedSpace, setSelectedSpace] = useState<EspacioServicio | null>(
    null,
  );
  const [calendarMonth, setCalendarMonth] = useState(
    format(new Date(), "yyyy-MM"),
  );
  const [occupancyRefreshKey, setOccupancyRefreshKey] = useState(0);
  const monthlyOccupancy = useMonthlySpaceOccupancy(
    selectedSpace?.id,
    calendarMonth,
    occupancyRefreshKey,
  );
  const [productosReserva, setProductosReserva] = useState<ProductoSeleccionado[]>([]);
  const posMixto = Boolean(puntoDeVenta.habilitar_citas && puntoDeVenta.habilitar_cafeteria);
  const totalProductos = posMixto ? productosReserva.reduce((s, p) => s + p.cantidad * p.precio_unitario - (p.monto_descuento || 0), 0) : 0;
  useEffect(() => { setProductosReserva([]); }, [sucursalId, sesionActiva?.id, puntoDeVenta.id, posMixto]);
  const [totalPrice, setTotalPrice] = useState(0);
  const [reglasEvaluadas, setReglasEvaluadas] = useState<any>(null);
  const [errorReserva, setErrorReserva] = useState("");
  const [errorReglas, setErrorReglas] = useState("");
  const [evaluandoReglas, setEvaluandoReglas] = useState(false);
  const [precioAutorizado, setPrecioAutorizado] = useState("");
  const [motivoPrecio, setMotivoPrecio] = useState("");
  const evaluacionRequest = useRef(0);
  const [descuentoDirecto, setDescuentoDirecto] = useState<number | string>("");
  const [descuentoServicio, setDescuentoServicio] = useState<number | string>("");
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [range, setRange] = useState<DateRange | undefined>(undefined);
  const [horaInicio, setHoraInicio] = useState<string | null>(null);
  const [horaFin, setHoraFin] = useState<string | null>(null);
  const [cantidadPeriodos, setCantidadPeriodos] = useState<number | null>(null);
  const fechaInicio = range?.from ? fmtDateWithoutTZ(range.from) : null;
  const fechaFin = range?.to ? fmtDateWithoutTZ(range.to) : null;
  const [isHoraFinManuallyModified, setIsHoraFinManuallyModified] =
    useState(false);
  const [daySelectionType, setDaySelectionType] = useState<"all" | "business">(
    "all",
  );

  const [clienteEditar, setClienteEditar] = useState<Cliente | null>(null);
  const [showClientModal, setShowClientModal] = useState(false);
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [clientesLoading, setClientesLoading] = useState(false);
  const [clientesError, setClientesError] = useState<string | null>(null);
  const [clientesReloadKey, setClientesReloadKey] = useState(0);
  const [clienteId, setClienteId] = useState<number | null>(null);
  const [usuarioId, setUsuarioId] = useState<number | null>(null);
  const [cuponCodigo, setCuponCodigo] = useState("");
  const [cuponAplicado, setCuponAplicado] = useState<Cupon | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState<"clients" | "summary">("clients");
  const [citasExistentes, setCitasExistentes] = useState<Cita[]>([]);
  const [citasSucursalId, setCitasSucursalId] = useState<number | null>(null);
  const [citasLoading, setCitasLoading] = useState(false);
  const [citasError, setCitasError] = useState<string | null>(null);
  const [citasReloadKey, setCitasReloadKey] = useState(0);
  const [espaciosDisponibles, setEspaciosDisponibles] = useState<{
    [k: number]: boolean;
  }>({});
  const [showAvailabilityModal, setShowAvailabilityModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [clientesAsociados, setClientesAsociados] = useState<number[]>([]);
  const [clientesAsociadosInfo, setClientesAsociadosInfo] = useState<
    ClienteAsociadoInfo[]
  >([]);
  const [showAgregarClientesModal, setShowAgregarClientesModal] =
    useState(false);
  const [usaTemporizador, setUsaTemporizador] = useState(false);
  const availabilityRequestRef = useRef(0);
  const categoriesRequestRef = useRef(0);
  const servicesRequestRef = useRef(0);
  const plansRequestRef = useRef(0);
  const spacesRequestRef = useRef(0);
  const citasRequestRef = useRef(0);
  const couponValidationRequestRef = useRef(0);
  const reservationRequestRef = useRef(0);
  const clientesRequestRef = useRef(0);
  const planConfigsRequestRef = useRef(0);
  // Esta instancia vive para UNA autoridad sucursal+sesión+POS (ver `key` en
  // NuevaCitaContent). Al desmontarse, toda respuesta en vuelo queda
  // invalidada por request-id y por mountedRef.
  const mountedRef = useRef(true);
  const [availabilityError, setAvailabilityError] = useState("");
  const [availabilityContextKey, setAvailabilityContextKey] = useState<
    string | null
  >(null);
  const [afiliacionCliente, setAfiliacionCliente] = useState<Afiliacion | null>(
    null,
  );
  const [usarHorasDomiciliado, setUsarHorasDomiciliado] = useState(false);

  // El descuento manual solo aplica a planes fijos; con temporizador el
  // backend lo rechaza antes del cierre (se aplica al finalizar).
  const descuentoNum =
    usaTemporizador || descuentoDirecto === ""
      ? 0
      : Number(descuentoDirecto) || 0;

  // "Todos los espacios" no es una Category real: la categoría canónica se resuelve
  // desde servicio.categoria contra el catálogo de la sucursal.
  const categoriesReady = categoriesSucursalId === sucursalId;
  const currentCategories = categoriesReady ? categories : EMPTY_CATEGORIES;
  const currentCitas =
    citasSucursalId === sucursalId ? citasExistentes : EMPTY_CITAS;
  const filterCategory =
    categoryFilter === ALL_FILTER
      ? null
      : (currentCategories.find((c) => c.id === categoryFilter) ?? null);
  const resolvedCategory = selectedService
    ? (currentCategories.find((c) => c.id === selectedService.categoria) ??
      filterCategory)
    : null;

  const subtotalServicio = (Number(reglasEvaluadas?.importe_oficina_virtual ?? totalPrice) || 0) + Number(reglasEvaluadas?.consumo?.precio_excedente || 0);
  const descuentoServicioNum = usaTemporizador ? 0 : Math.min(subtotalServicio, Math.max(0, Number(descuentoServicio) || 0));
  const subtotalNeto = Math.max(0, subtotalServicio - descuentoServicioNum) + totalProductos;
  const descuentoGlobal = Math.min(descuentoNum, subtotalNeto);
  const globalServicio = Math.min(descuentoGlobal, Math.max(0, subtotalServicio - descuentoServicioNum));
  const globalProductos = descuentoGlobal - globalServicio;
  const calcularTotal = () => {
    let sub =
        (Number(reglasEvaluadas?.importe_oficina_virtual ?? totalPrice) || 0) +
        Number(reglasEvaluadas?.consumo?.precio_excedente || 0) - descuentoServicioNum,
      desc = globalServicio;
    if (cuponAplicado) {
      if (cuponAplicado.descuento_monto)
        desc += Number(cuponAplicado.descuento_monto) || 0;
      else if (cuponAplicado.descuento_porcentaje)
        desc +=
          (Math.max(sub - desc, 0) *
            (Number(cuponAplicado.descuento_porcentaje) || 0)) /
          100;
    }
    return Math.max(sub - desc, 0);
  };
  const precioSugerido = calcularTotal();
  const totalFinal =
    (precioAutorizado !== "" ? Math.max(0, Number(precioAutorizado) - descuentoServicioNum - globalServicio) : precioSugerido) + totalProductos - globalProductos;
  useEffect(() => {
    ++evaluacionRequest.current;
    setReglasEvaluadas(null);
    setErrorReglas("");
    setEvaluandoReglas(false);
    setPrecioAutorizado("");
    setMotivoPrecio("");
  }, [
    selectedPlan?.id,
    selectedSpace?.id,
    clienteId,
    fechaInicio,
    fechaFin,
    horaInicio,
    horaFin,
    daySelectionType,
    cantidadPeriodos,
  ]);
  const evaluarCondiciones = async () => {
    const cliente = clientes.find((c) => c.id === clienteId);
    if (
      !cliente?.usuario ||
      !selectedPlan ||
      !resolvedCategory ||
      !fechaInicio ||
      !fechaFin ||
      !horaInicio
    ) {
      setErrorReglas(
        "Selecciona cliente, plan, fechas y horario para validar.",
      );
      return;
    }
    const revision = ++evaluacionRequest.current;
    setEvaluandoReglas(true);
    setErrorReglas("");
    try {
      const result = await evaluarPlanReserva(
        {
          cliente: cliente.usuario.id,
          precio_plan_servicio: selectedPlan.id,
          espacio_servicio: selectedService?.requiere_espacio
            ? selectedSpace?.id
            : null,
          categoria: resolvedCategory.id,
          fecha_inicio: fechaInicio,
          fecha_fin: fechaFin,
          hora_inicio: horaInicio,
          hora_fin: usaTemporizador ? null : horaFin,
          cantidad_periodos: cantidadPeriodos,
          dias_habiles: daySelectionType === "business",
          sucursal_id: sucursalId,
          sesion_pos: sesionActiva?.id,
        },
        operadorToken,
      );
      if (mountedRef.current && revision === evaluacionRequest.current)
        setReglasEvaluadas(result);
    } catch (e: any) {
      if (mountedRef.current && revision === evaluacionRequest.current)
        setErrorReglas(
          Object.values(
            e.response?.data || { error: "No se pudo validar el plan." },
          )
            .flat()
            .join(" "),
        );
    } finally {
      if (mountedRef.current && revision === evaluacionRequest.current)
        setEvaluandoReglas(false);
    }
  };

  // Cotizar automáticamente al completar la selección; invalidar respuestas anteriores.
  useEffect(() => {
    if (selectedService?.tipo_servicio !== 'oficina_virtual' || !clienteId ||
        !selectedPlan || !fechaInicio || !fechaFin || !horaInicio || !horaFin ||
        !cantidadPeriodos || (requireOperatorPin && !operadorToken)) return;
    setReglasEvaluadas(null);
    void evaluarCondiciones();
    return () => { ++evaluacionRequest.current; };
  }, [selectedService?.tipo_servicio, selectedPlan?.id, clienteId, clientes,
      fechaInicio, fechaFin, horaInicio, horaFin, cantidadPeriodos, daySelectionType,
      sucursalId, sesionActiva?.id, operadorToken, requireOperatorPin]);

  const addBusinessDays = (startDate: Date, n: number) => {
    const r = new Date(startDate);
    let added = 0;
    while (added < n) {
      r.setDate(r.getDate() + 1);
      if (r.getDay() !== 0 && r.getDay() !== 6) added++;
    }
    return r;
  };

  const currentAvailabilityContextKey =
    selectedService?.requiere_espacio && fechaInicio && fechaFin && horaInicio
      ? [
          selectedService.id,
          selectedPlan?.id ?? "",
          fechaInicio,
          fechaFin,
          horaInicio,
          horaFin || "23:59:59",
          cantidadPeriodos ?? "",
          daySelectionType,
          usaTemporizador,
        ].join("|")
      : null;
  const selectedSpaceHasConflict =
    !!selectedSpace &&
    !!currentAvailabilityContextKey &&
    availabilityContextKey === currentAvailabilityContextKey &&
    espaciosDisponibles[selectedSpace.id] === false;

  const verificarDisponibilidadEspacios = useCallback(async () => {
    const requestId = ++availabilityRequestRef.current;
    setAvailabilityError("");
    if (
      !selectedService?.requiere_espacio ||
      !fechaInicio ||
      !fechaFin ||
      !horaInicio
    ) {
      setEspaciosDisponibles({});
      setAvailabilityContextKey(null);
      return;
    }
    const requestContextKey = [
      selectedService.id,
      selectedPlan?.id ?? "",
      fechaInicio,
      fechaFin,
      horaInicio,
      horaFin || "23:59:59",
      cantidadPeriodos ?? "",
      daySelectionType,
      usaTemporizador,
    ].join("|");
    setEspaciosDisponibles({});
    setAvailabilityContextKey(null);
    try {
      const disponibles = await fetchEspaciosDisponiblesPorServicio(
        selectedService.id,
        fechaInicio,
        horaInicio,
        horaFin || "23:59:59",
        fechaFin,
        daySelectionType === "business",
        usaTemporizador,
      );
      if (requestId !== availabilityRequestRef.current) return;
      const ids = new Set(disponibles.map((e) => e.id));
      const disp: { [k: number]: boolean } = {};
      spaces.forEach((e) => {
        disp[e.id] = ids.has(e.id);
      });
      setEspaciosDisponibles(disp);
      setAvailabilityContextKey(requestContextKey);
    } catch {
      if (requestId !== availabilityRequestRef.current) return;
      setEspaciosDisponibles({});
      setAvailabilityError(
        "No se pudo consultar la disponibilidad. Reintenta la consulta.",
      );
      setAvailabilityContextKey(null);
    }
  }, [
    fechaInicio,
    fechaFin,
    horaInicio,
    horaFin,
    cantidadPeriodos,
    spaces,
    selectedService,
    selectedPlan,
    daySelectionType,
    usaTemporizador,
  ]);

  useEffect(() => {
    const requestId = ++citasRequestRef.current;
    setCitasExistentes([]);
    setCitasSucursalId(null);
    setCitasError(null);
    if (!sucursalId) {
      setCitasLoading(false);
      return;
    }
    setCitasLoading(true);
    fetchCitas(sucursalId)
      .then((citas) => {
        if (requestId !== citasRequestRef.current) return;
        setCitasExistentes(citas || []);
        setCitasSucursalId(sucursalId);
        setCitasLoading(false);
      })
      .catch(() => {
        if (requestId !== citasRequestRef.current) return;
        setCitasExistentes([]);
        setCitasSucursalId(sucursalId);
        setCitasLoading(false);
        setCitasError(
          "No se pudieron cargar las reservas existentes de la sucursal. Intenta nuevamente.",
        );
      });
  }, [sucursalId, citasReloadKey]);
  // Catálogo de categorías acotado a la sucursal. Si la carga acotada falla NO se
  // recurre al catálogo global: el catálogo queda vacío, no se marca la sucursal
  // como cargada (por lo que no se piden servicios) y se muestra el error.
  useEffect(() => {
    const requestId = ++categoriesRequestRef.current;
    setCategories([]);
    setCategoriesSucursalId(null);
    setCategoriesError(null);
    if (!sucursalId) {
      setCategoriesLoading(false);
      return;
    }
    setCategoriesLoading(true);
    fetchCategories(sucursalId)
      .then((d: Categoria[]) => {
        if (requestId !== categoriesRequestRef.current) return;
        const activas = (d || []).filter((c: Categoria) => c.estado);
        setCategories((prev) =>
          sameCategories(prev, activas) ? prev : activas,
        );
        setCategoriesSucursalId(sucursalId);
        setCategoriesLoading(false);
      })
      .catch((error: unknown) => {
        if (requestId !== categoriesRequestRef.current) return;
        setCategories([]);
        setCategoriesSucursalId(null);
        setCategoriesLoading(false);
        setCategoriesError(
          "No se pudo cargar el catálogo de la sucursal. Intenta nuevamente.",
        );
        /* Browser diagnostics omitted. */
      });
  }, [sucursalId, categoriesReloadKey]);
  useSpaceAvailability(() => { void verificarDisponibilidadEspacios(); });
  useEffect(() => {
    verificarDisponibilidadEspacios();
  }, [verificarDisponibilidadEspacios]);

  const clearDependentSelections = useCallback(() => {
    setSelectedService(null);
    setPricePlans([]);
    setSelectedPlan(null);
    setCantidadPeriodos(null);
    setSpaces([]);
    setSelectedSpace(null);
    setRange(undefined);
    setHoraInicio(null);
    setHoraFin(null);
    setEspaciosDisponibles({});
    setAvailabilityContextKey(null);
    setTotalPrice(0);
    setUsaTemporizador(false);
    setCuponAplicado(null);
    setCuponCodigo("");
  }, []);

  // Al desmontar (cambio de sucursal o salida de la página) se invalida toda
  // respuesta en vuelo: ninguna continuación asíncrona puede completar una
  // reserva ni refrescar datos fuera de su sucursal.
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      availabilityRequestRef.current += 1;
      categoriesRequestRef.current += 1;
      servicesRequestRef.current += 1;
      plansRequestRef.current += 1;
      spacesRequestRef.current += 1;
      citasRequestRef.current += 1;
      clientesRequestRef.current += 1;
      couponValidationRequestRef.current += 1;
      reservationRequestRef.current += 1;
      planConfigsRequestRef.current += 1;
    };
  }, []);

  // Si la categoría filtrada deja de existir en la sucursal, volver a "Todos los espacios".
  useEffect(() => {
    if (
      categoryFilter === ALL_FILTER ||
      currentCategories.some((c) => c.id === categoryFilter)
    )
      return;
    setCategoryFilter(ALL_FILTER);
    setServices([]);
    clearDependentSelections();
    setStep(STEP_SERVICE);
  }, [currentCategories, categoryFilter, clearDependentSelections]);

  // Servicios visibles: todas las categorías activas (en paralelo, deduplicadas) o la filtrada.
  // Las respuestas obsoletas (cambio de filtro/sucursal) se descartan por requestId.
  useEffect(() => {
    const requestId = ++servicesRequestRef.current;
    // Fail-closed: sin el catálogo de categorías DE ESTA sucursal no se pide
    // ningún servicio, y un filtro que no pertenece a él no genera llamadas
    // (nunca [categoríaVieja, sucursalNueva]).
    if (!sucursalId || !categoriesReady) {
      setServices([]);
      setServicesLoading(false);
      setServicesError(null);
      return;
    }
    const targets =
      categoryFilter === ALL_FILTER
        ? currentCategories.map((c) => c.id)
        : currentCategories.some((c) => c.id === categoryFilter)
          ? [categoryFilter]
          : [];
    if (targets.length === 0) {
      setServices([]);
      setServicesLoading(false);
      setServicesError(null);
      return;
    }
    setServicesLoading(true);
    setServicesError(null);
    Promise.all(targets.map((id) => fetchServiciosByCategory(id, sucursalId)))
      .then((results) => {
        if (requestId !== servicesRequestRef.current) return;
        const seen = new Set<number>();
        const merged: Servicio[] = [];
        results.forEach((list) =>
          (list || []).forEach((s) => {
            if (s.estado && !seen.has(s.id)) {
              seen.add(s.id);
              merged.push(s);
            }
          }),
        );
        setServices(merged);
        setServicesLoading(false);
      })
      .catch(() => {
        if (requestId !== servicesRequestRef.current) return;
        setServices([]);
        setServicesLoading(false);
        setServicesError(
          "No se pudieron cargar los servicios. Intenta nuevamente.",
        );
      });
  }, [
    categoryFilter,
    sucursalId,
    categoriesReady,
    currentCategories,
    servicesReloadKey,
  ]);
  useEffect(() => {
    const requestId = ++clientesRequestRef.current;
    setClientesLoading(true);
    setClientesError(null);
    fetchClientes()
      .then((d) => {
        if (requestId !== clientesRequestRef.current) return;
        setClientes((d || []).slice().sort((a, b) => a.id - b.id));
        setClientesLoading(false);
      })
      .catch(() => {
        if (requestId !== clientesRequestRef.current) return;
        setClientes([]);
        setClientesLoading(false);
        setClientesError(
          "No se pudieron cargar los clientes. Intenta nuevamente.",
        );
      });
  }, [clientesReloadKey]);
  useEffect(() => {
    if (!clienteId) {
      setAfiliacionCliente(null);
      setUsarHorasDomiciliado(false);
      return;
    }
    const cl = clientes.find((c) => c.id === clienteId);
    if (cl?.usuario?.id) {
      fetchAfiliacionByCliente(cl.usuario.id)
        .then((af) => {
          if (
            af &&
            af.formulario_habilitado &&
            Number(af.horas_gratuitas_disponibles) > 0
          ) {
            setAfiliacionCliente(af);
          } else {
            setAfiliacionCliente(null);
            setUsarHorasDomiciliado(false);
          }
        })
        .catch(() => {
          setAfiliacionCliente(null);
          setUsarHorasDomiciliado(false);
        });
    }
  }, [clienteId, clientes]);
  useEffect(() => {
    const requestId = ++plansRequestRef.current;
    setPlansError(null);
    if (!selectedService || !sucursalId) {
      setPricePlans([]);
      setPlansLoading(false);
      return;
    }
    setPlansLoading(true);
    fetchPrecioPlanesServicios()
      .then((all) => {
        if (requestId !== plansRequestRef.current) return;
        setPricePlans(
          (all || []).filter(
            (p) => p.servicio === selectedService.id && p.estado,
          ),
        );
        setPlansLoading(false);
      })
      .catch(() => {
        if (requestId !== plansRequestRef.current) return;
        setPricePlans([]);
        setPlansLoading(false);
        setPlansError(
          "No se pudieron cargar los planes del servicio. Intenta nuevamente.",
        );
      });
  }, [selectedService, sucursalId, plansReloadKey]);
  useEffect(() => {
    if (!selectedPlan) return;
    setTotalPrice(Number(selectedPlan.precio_inicial) || 0);
    const cfg = planConfigs.find(
      (c) => c.id === selectedPlan.plan_duracion_config,
    );
    if (cfg) {
      setUsaTemporizador(cfg.usa_temporizador);
      if (cfg.usa_temporizador) {
        setHoraFin(null);
        setIsHoraFinManuallyModified(false);
      }
    } else setUsaTemporizador(false);
  }, [selectedPlan, planConfigs]);
  useEffect(() => {
    setIsHoraFinManuallyModified(false);
  }, [horaInicio, selectedPlan]);
  useEffect(() => {
    couponValidationRequestRef.current += 1;
    setCuponAplicado(null);
  }, [
    sucursalId,
    clienteId,
    usuarioId,
    selectedService?.id,
    selectedPlan?.id,
    cuponCodigo,
  ]);
  useEffect(() => {
    if (
      !selectedPlan ||
      !range?.from ||
      cantidadPeriodos === null ||
      !Number.isInteger(cantidadPeriodos) ||
      cantidadPeriodos <= 0
    )
      return;
    const cfg = planConfigs.find(
      (c) => c.id === selectedPlan.plan_duracion_config,
    );
    if (!cfg) return;
    if (selectedService?.tipo_servicio === 'oficina_virtual') {
      if (!cfg.dias || !cfg.horas || cfg.usa_temporizador) return;
      const dias = cfg.dias * cantidadPeriodos - 1;
      setRange({from: range.from, to: daySelectionType === 'business'
        ? addBusinessDays(range.from, dias) : addDays(range.from, dias)});
      const hora = horaInicio || '00:00';
      if (!horaInicio) setHoraInicio(hora);
      setHoraFin(format(addHours(parse(hora, 'HH:mm', range.from), cfg.horas), 'HH:mm'));
      const iniciales = Math.min(cantidadPeriodos, selectedPlan.duracion_inicial || 1);
      setTotalPrice(iniciales * Number(selectedPlan.precio_inicial) + (cantidadPeriodos - iniciales) * Number(selectedPlan.precio_posterior ?? selectedPlan.precio_inicial));
      return;
    }
    if (cfg.usa_temporizador) {
      setHoraFin(null);
      setTotalPrice(0);
      return;
    }
    if (cfg.unidad === "dia" && cfg.dias) {
      const td = cantidadPeriodos * cfg.dias;
      const ne =
        daySelectionType === "business"
          ? addBusinessDays(range.from, td - 1)
          : addDays(range.from, td - 1);
      setRange({ from: range.from, to: ne });
      if (
        selectedPlan.precio_posterior != null &&
        selectedPlan.duracion_inicial
      ) {
        const di = selectedPlan.duracion_inicial,
          pi = Number(selectedPlan.precio_inicial) || 0,
          pp = Number(selectedPlan.precio_posterior) || 0;
        const ic = Math.min(cantidadPeriodos, di),
          pc = Math.max(cantidadPeriodos - di, 0);
        setTotalPrice(ic * pi + pc * pp);
      } else
        setTotalPrice(
          (Number(selectedPlan.precio_inicial) || 0) * cantidadPeriodos,
        );
      if (
        !cfg.usa_temporizador &&
        horaInicio &&
        cfg.horas !== null &&
        !isHoraFinManuallyModified
      ) {
        const st = parse(horaInicio, "HH:mm", new Date());
        setHoraFin(format(addHours(st, cfg.horas), "HH:mm"));
      }
      return;
    }
    if (cfg.unidad === "hora" && cfg.horas !== null && cfg.dias !== null) {
      const td = cantidadPeriodos * cfg.dias;
      setRange({
        from: range.from,
        to:
          daySelectionType === "business"
            ? addBusinessDays(range.from, td - 1)
            : addDays(range.from, td - 1),
      });
      if (horaInicio && !isHoraFinManuallyModified)
        setHoraFin(
          format(
            addHours(parse(horaInicio, "HH:mm", range.from), cfg.horas),
            "HH:mm",
          ),
        );
      if (!horaInicio || !horaFin) {
        setTotalPrice(0);
        return;
      }
      // hora_fin <= hora_inicio => termina al día siguiente (misma regla que el backend).
      const hSel = dailyHours(range.from, horaInicio, horaFin);
      let tot = 0;
      if (selectedPlan.precio_posterior && selectedPlan.duracion_inicial) {
        let rem = selectedPlan.duracion_inicial;
        for (let d = 0; d < td; d++) {
          if (rem <= 0)
            tot += hSel * (Number(selectedPlan.precio_posterior) || 0);
          else {
            const ih = Math.min(hSel, rem),
              ph = hSel - ih;
            tot +=
              ih * (Number(selectedPlan.precio_inicial) || 0) +
              ph * (Number(selectedPlan.precio_posterior) || 0);
            rem -= ih;
          }
        }
      } else
        tot =
          ((Number(selectedPlan.precio_inicial) || 0) / cfg.horas) * hSel * td;
      setTotalPrice(Math.round(tot) || 0);
    }
  }, [
    selectedPlan,
    selectedService?.tipo_servicio,
    range?.from,
    cantidadPeriodos,
    horaInicio,
    horaFin,
    planConfigs,
    isHoraFinManuallyModified,
    daySelectionType,
  ]);
  useEffect(() => {
    const requestId = ++planConfigsRequestRef.current;
    const isCurrent = () =>
      mountedRef.current && requestId === planConfigsRequestRef.current;
    setPlanConfigsError(null);
    setPlanConfigsLoading(true);
    fetchPlanDuracionConfigs()
      .then((c) => {
        if (!isCurrent()) return;
        setPlanConfigs((c || []).filter((x) => x.estado));
        setPlanConfigsLoading(false);
      })
      .catch(() => {
        if (!isCurrent()) return;
        setPlanConfigs([]);
        setPlanConfigsLoading(false);
        setPlanConfigsError(
          "No se pudo cargar la configuración de los planes. Intenta nuevamente.",
        );
      });
  }, [planConfigsReloadKey]);
  useEffect(() => {
    if (step === STEP_DATES && selectedPlan)
      setCantidadPeriodos((actual) => actual ?? 1);
  }, [step, selectedPlan]);
  useEffect(() => {
    const requestId = ++spacesRequestRef.current;
    setEspaciosDisponibles({});
    setAvailabilityContextKey(null);
    setSpacesError(null);
    if (!selectedService) {
      setSpaces([]);
      setSpacesLoading(false);
      return;
    }
    if (selectedService.requiere_espacio) {
      setSpacesLoading(true);
      fetchEspacioServicios(selectedService.id, sucursalId || undefined)
        .then((all) => {
          if (requestId !== spacesRequestRef.current) return;
          const fs = (all || []).filter((e) => e.estado);
          setSpaces(fs);
          setSpacesLoading(false);
          const ia: { [k: number]: boolean } = {};
          fs.forEach((s) => {
            ia[s.id] = false;
          });
          setEspaciosDisponibles(ia);
        })
        .catch(() => {
          if (requestId !== spacesRequestRef.current) return;
          setSpaces([]);
          setSpacesLoading(false);
          setSpacesError(
            "No se pudieron cargar los espacios del servicio. Intenta nuevamente.",
          );
        });
    } else {
      setSpaces([]);
      setSelectedSpace(null);
      setSpacesLoading(false);
    }
  }, [selectedService, sucursalId, spacesReloadKey]);

  const validarCupon = async () => {
    const codigo = cuponCodigo.trim();
    if (!codigo) return;
    const requestId = ++couponValidationRequestRef.current;
    const isCurrent = () => requestId === couponValidationRequestRef.current;
    try {
      const cupones = await fetchCupones();
      if (!isCurrent()) return;
      const cf = cupones.find(
        (c) => c.codigo.trim().toUpperCase() === codigo.toUpperCase(),
      );
      if (!cf) {
        alert("Cupón no válido.");
        setCuponAplicado(null);
        return;
      }
      const now = new Date();
      if (!(
        new Date(cf.fecha_inicio) <= now && now <= new Date(cf.fecha_fin)
      )) {
        alert("El cupón está fuera de vigencia.");
        setCuponAplicado(null);
        return;
      }
      if (!clienteId) {
        alert("Debe seleccionar un cliente antes de validar un cupón.");
        setCuponAplicado(null);
        return;
      }
      if (cf.uso_unico_por_cliente) {
        if (!usuarioId) {
          alert("Debe seleccionar un cliente.");
          setCuponAplicado(null);
          return;
        }
        try {
          const usados = await fetchCuponesUsados();
          if (!isCurrent()) return;
          if (
            usados.find(
              (u) =>
                Number(u.cliente) === Number(usuarioId) &&
                Number(u.cupon) === Number(cf.id),
            )
          ) {
            alert("Cupón ya usado.");
            setCuponAplicado(null);
            return;
          }
        } catch {
          if (isCurrent()) {
            alert("No se pudo validar el cupón.");
            setCuponAplicado(null);
          }
          return;
        }
      }
      if (!isCurrent()) return;
      if (
        cf.plan_aplicable?.id &&
        (!selectedPlan || selectedPlan.id !== cf.plan_aplicable.id)
      ) {
        alert(`Cupón solo para "${cf.plan_aplicable.plan_nombre}".`);
        setCuponAplicado(null);
        return;
      }
      setCuponAplicado(cf);
    } catch {
      if (isCurrent()) {
        alert("No se pudo validar el cupón.");
        setCuponAplicado(null);
      }
    }
  };

  const handleSelectFilter = (filter: CategoryFilter) => {
    if (filter === categoryFilter) return;
    setCategoryFilter(filter);
    setServices([]);
    clearDependentSelections();
    setStep(STEP_SERVICE);
  };
  const handleSelectService = (srv: Servicio) => {
    const cambia = selectedService?.id !== srv.id;
    setSelectedService(srv);
    setStep(STEP_PLAN);
    if (cambia) {
      setPricePlans([]);
      setSelectedPlan(null);
      setCantidadPeriodos(null);
      setSpaces([]);
      setSelectedSpace(null);
    } else if (!srv.requiere_espacio) {
      setSpaces([]);
      setSelectedSpace(null);
    }
  };
  const handleSelectPlan = (plan: PrecioPlanServicio) => {
    if (selectedPlan?.id !== plan.id) setCantidadPeriodos(1);
    setSelectedPlan(plan);
    setStep(selectedService?.requiere_espacio ? STEP_SPACE : STEP_DATES);
    if (!selectedService?.requiere_espacio) setSelectedSpace(null);
  };
  const handleSelectSpace = (esp: EspacioServicio) => {
    setOccupancyRefreshKey((key) => key + 1);
    setSelectedSpace(esp);
    setStep(STEP_DATES);
  };

  const handleAgregarClientesAsociados = (ids: number[]) => {
    const nuevos = clientes.filter((c) => ids.includes(c.id) && c.usuario &&
      c.usuario.id !== usuarioId && !clientesAsociados.includes(c.usuario.id));
    setClientesAsociados(prev => Array.from(new Set([...prev, ...nuevos.map(c => c.usuario!.id)])));
    setClientesAsociadosInfo(prev => [...prev, ...nuevos.map(c => ({
      id: c.id, userId: c.usuario!.id, first_name: c.usuario!.first_name,
      last_name: c.usuario!.last_name, email: c.usuario!.email,
    }))]);
    setShowAgregarClientesModal(false);
  };
  useEffect(() => {
    if (!usuarioId) return;
    setClientesAsociados(prev => prev.filter(id => id !== usuarioId));
    setClientesAsociadosInfo(prev => prev.filter(c => c.userId !== usuarioId));
  }, [usuarioId]);
  const handleRemoverClienteAsociado = (id: number) => {
    const inf = clientesAsociadosInfo.find((x) => x.id === id);
    if (inf?.userId)
      setClientesAsociados(clientesAsociados.filter((x) => x !== inf.userId));
    setClientesAsociadosInfo(clientesAsociadosInfo.filter((x) => x.id !== id));
  };

  const initialServiceApplied = useRef(false);
  const initialSpaceApplied = useRef(false);
  useEffect(() => {
    if (!espacioInicial || initialServiceApplied.current || !services.length)
      return;
    const servicio = services.find((s) => s.id === espacioInicial.servicio);
    if (servicio) {
      initialServiceApplied.current = true;
      handleSelectService(servicio);
    }
  }, [services, espacioInicial]);
  useEffect(() => {
    if (!espacioInicial || initialSpaceApplied.current || !selectedPlan) return;
    const espacio = spaces.find((s) => s.id === espacioInicial.id);
    if (espacio) {
      initialSpaceApplied.current = true;
      handleSelectSpace(espacio);
      if (fechaInicial) {
        const d = new Date(fechaInicial + "T12:00:00");
        setRange({ from: d, to: d });
      }
    }
  }, [spaces, selectedPlan, espacioInicial, fechaInicial]);

  const handleCreateReservation = async (
    accion: "guardar" | "despues" | "cobrar" = "guardar",
  ) => {
    if (
      !resolvedCategory ||
      !selectedService ||
      !selectedPlan ||
      !fechaInicio ||
      !fechaFin ||
      !horaInicio
    ) {
      alert("Complete todos los campos.");
      return;
    }
    if (!Number.isInteger(cantidadPeriodos) || Number(cantidadPeriodos) <= 0) {
      alert(
        "La cantidad de períodos debe ser un número entero mayor que cero.",
      );
      return;
    }
    if (requireOperatorPin && !operadorToken) {
      alert(
        "Selecciona el operador de caja e ingresa su PIN antes de registrar la reserva.",
      );
      return;
    }
    if (!usaTemporizador && !horaFin) {
      alert("Debe completar la hora final.");
      return;
    }
    if (selectedService.requiere_espacio) {
      if (!selectedSpace) {
        alert("Se requiere un espacio físico.");
        return;
      }
      if (
        availabilityContextKey !== currentAvailabilityContextKey ||
        espaciosDisponibles[selectedSpace.id] !== true
      ) {
        setShowAvailabilityModal(true);
        return;
      }
    } else setSelectedSpace(null);
    if (!clienteId) {
      alert("Debe agregar un cliente.");
      return;
    }
    const requestId = ++reservationRequestRef.current;
    // Autoridad compuesta capturada al iniciar: sucursal + sesión POS + punto.
    const requestedSucursal = sucursalId;
    const requestedSesion = sesionActiva?.id ?? null;
    const requestedPos = puntoDeVenta?.id ?? null;
    setLoading(true);
    const cl = clientes.find((c) => c.id === clienteId);
    if (!cl?.usuario?.id) {
      alert("Cliente inválido.");
      setLoading(false);
      return;
    }
    const isCurrent = () =>
      mountedRef.current &&
      requestId === reservationRequestRef.current &&
      requestedSucursal === sucursalId &&
      requestedSesion === (sesionActiva?.id ?? null) &&
      requestedPos === (puntoDeVenta?.id ?? null);
    try {
      // La sucursal y la sesión POS explícitas son la autoridad de la operación:
      // el backend valida que la caja sea propia/abierta y de la misma sucursal.
      // subtotal/total NO se envían: el servidor los deriva de las sesiones
      // canónicas; solo viaja el descuento manual (0 con temporizador).
      const payload = {
        ...(posMixto ? { productos: productosReserva.map(({producto, cantidad, monto_descuento}) => ({producto, cantidad, monto_descuento: (monto_descuento || 0).toFixed(2)})) } : {}),
        cliente: cl.usuario.id,
        precio_plan_servicio: selectedPlan.id,
        espacio_servicio: selectedService.requiere_espacio
          ? selectedSpace?.id || null
          : null,
        categoria: resolvedCategory.id,
        fecha_inicio: fechaInicio,
        fecha_fin: fechaFin,
        hora_inicio: horaInicio,
        hora_fin: usaTemporizador ? null : horaFin,
        cantidad_periodos: cantidadPeriodos,
        descuento_directo: descuentoGlobal,
        descuento_servicio: descuentoServicioNum,
        cupon: cuponAplicado?.id ?? null,
        dias_habiles: daySelectionType === "business",
        clientes_asociados: clientesAsociados,
        usar_horas_domiciliado: usarHorasDomiciliado,
        sucursal_id: requestedSucursal,
        sesion_pos: sesionActiva?.id ?? null,
      };
      // El grant del operador es obligatorio: sin él el servicio no llama al backend.
      const creada = await createCita(
        {
          ...payload,
          ...(precioAutorizado !== ""
            ? { precio_final: precioAutorizado, motivo_precio: motivoPrecio }
            : {}),
        },
        operadorToken,
      );
      if (isCurrent() && onReservaCreada) {
        onReservaCreada(creada, accion);
        return;
      }
      if (!isCurrent()) return;
      const citasRequestId = ++citasRequestRef.current;
      setCitasLoading(true);
      setCitasError(null);
      let citas: Cita[] | null = null;
      try {
        citas = (await fetchCitas(requestedSucursal || undefined)) || [];
      } catch {
        citas = null;
      }
      if (!isCurrent() || citasRequestId !== citasRequestRef.current) return;
      setCitasLoading(false);
      if (citas === null)
        setCitasError(
          "No se pudieron cargar las reservas existentes de la sucursal. Intenta nuevamente.",
        );
      else {
        setCitasExistentes(citas);
        setCitasSucursalId(requestedSucursal ?? null);
      }
      setProductosReserva([]);
      setShowSuccessModal(true);
      setDescuentoDirecto("");
      setDescuentoServicio("");
      setClientesAsociados([]);
      setClientesAsociadosInfo([]);
      setUsarHorasDomiciliado(false);
    } catch (e: any) {
      if (isCurrent()) {
        const legible = (v: any): string =>
          typeof v === "string"
            ? v
            : v && typeof v === "object"
              ? Object.values(v).map(legible).join(" ")
              : "";
        const mensaje =
          legible(e.response?.data) || "No se pudo guardar la reserva.";
        if (onReservaCreada) setErrorReserva(mensaje);
        else alert(mensaje);
      }
    } finally {
      if (isCurrent()) setLoading(false);
    }
  };

  const handleModalClose = () => {
    setShowSuccessModal(false);
    setStep(STEP_SERVICE);
    clearDependentSelections();
    setClienteId(null);
    setUsuarioId(null);
    setClientesAsociados([]);
    setClientesAsociadosInfo([]);
  };
  const handleOpenClientModal = () => {
    setClienteEditar(null);
    setShowClientModal(true);
  };
  const handleCloseClientModal = () => {
    setShowClientModal(false);
    setClienteEditar(null);
  };
  const handleClienteGuardado = (cliente: Cliente) => {
    setClientes((prev) =>
      prev.some((c) => c.id === cliente.id)
        ? prev.map((c) => (c.id === cliente.id ? cliente : c))
        : [...prev, cliente],
    );
    if (!clienteEditar) {
      setClienteId(cliente.id);
      setUsuarioId(cliente.usuario?.id ?? null);
    }
  };

  const filteredClients = clientes.filter((c) => {
    const s = searchTerm.toLowerCase();
    return (
      c.usuario?.first_name?.toLowerCase().includes(s) ||
      c.usuario?.last_name?.toLowerCase().includes(s) ||
      c.usuario?.email?.toLowerCase().includes(s) ||
      c.ci_nit?.toLowerCase().includes(s) ||
      c.telefono?.toLowerCase().includes(s)
    );
  });

  const sectionState = (id: number) =>
    step === id ? "current" : step > id ? "done" : "";

  const espacioConfirmado =
    !selectedService?.requiere_espacio ||
    (!!selectedSpace &&
      availabilityContextKey === currentAvailabilityContextKey &&
      espaciosDisponibles[selectedSpace.id] === true);
  const periodosValidos =
    Number.isInteger(cantidadPeriodos) && Number(cantidadPeriodos) > 0;
  // Con PIN requerido, el grant del operador forma parte de la autoridad de
  // la operación: sin él no hay nada que confirmar (no solo un guard al enviar).
  const operadorRequerido = requireOperatorPin && !operadorToken;
  const configSeleccionada = planConfigs.find((p) => p.id === selectedPlan?.plan_duracion_config);
  const duracionVirtualInvalida = selectedService?.tipo_servicio === 'oficina_virtual' &&
    (!configSeleccionada?.dias || !configSeleccionada?.horas ||
      configSeleccionada.horas > 24 || configSeleccionada.usa_temporizador);
  const isReadyToConfirm = !!(
    clienteId &&
    resolvedCategory &&
    selectedService &&
    selectedPlan &&
    fechaInicio &&
    fechaFin &&
    horaInicio &&
    periodosValidos &&
    !loading &&
    espacioConfirmado &&
    (usaTemporizador || horaFin) &&
    !operadorRequerido &&
    !duracionVirtualInvalida &&
    (selectedService?.tipo_servicio !== 'oficina_virtual' || reglasEvaluadas?.importe_oficina_virtual != null) &&
    (!onReservaCreada ||
      !Object.keys(
        planConfigs.find((p) => p.id === selectedPlan?.plan_duracion_config)
          ?.reglas_reserva || {},
      ).length ||
      !!reglasEvaluadas)
  );

  const showSelectionPlane = step <= STEP_PLAN;
  const showPlanSection =
    showSelectionPlane && step >= STEP_PLAN && !!selectedService;
  const showSpaceSection =
    !showSelectionPlane &&
    !!selectedPlan &&
    !!selectedService?.requiere_espacio;
  const showDatesSection =
    !showSelectionPlane && step >= STEP_DATES && !!selectedPlan;

  return (
    <>
      <style>{css}</style>
      {errorReserva && (
        <p role="alert" className="rw-error">
          {errorReserva}
        </p>
      )}
      <div
        className={`ncp ncp-scroll ${onReservaCreada ? "ncp-workspace" : ""}`}
        style={{
          display: "flex",
          height: "100%",
          minHeight: 0,
          background: C.canvas,
          overflowX: "clip",
          fontFamily: "'DM Sans',sans-serif",
        }}
      >
        {onReservaCreada && (
          <aside className="ncp-clients-column">
            <h2>Cliente y plan</h2>{" "}
            {
              <div style={{ padding: "14px" }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 8,
                    marginBottom: 10,
                  }}
                >
                  <div>
                    <p
                      style={{
                        fontSize: ".82rem",
                        fontWeight: 900,
                        color: C.navy,
                      }}
                    >
                      Directorio de clientes
                    </p>
                    <p
                      className="ncp-num"
                      style={{ fontSize: ".7rem", color: C.muted }}
                    >
                      {filteredClients.length} cliente
                      {filteredClients.length === 1 ? "" : "s"} disponible
                      {filteredClients.length === 1 ? "" : "s"}
                    </p>
                  </div>
                  {searchTerm && (
                    <button
                      type="button"
                      onClick={() => setSearchTerm("")}
                      style={{
                        border: 0,
                        background: "transparent",
                        fontSize: ".7rem",
                        fontWeight: 800,
                        color: C.secondary,
                        cursor: "pointer",
                        minHeight: 32,
                      }}
                    >
                      Limpiar
                    </button>
                  )}
                </div>
                <div
                  style={{ display: "flex", gap: "8px", marginBottom: "12px" }}
                >
                  <button
                    type="button"
                    onClick={handleOpenClientModal}
                    title="Nuevo Cliente"
                    aria-label="Nuevo cliente"
                    style={{
                      width: "40px",
                      height: "40px",
                      borderRadius: "10px",
                      border: "none",
                      background: `linear-gradient(135deg,${C.secondary},${C.secondaryDark})`,
                      color: "#fff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      flexShrink: 0,
                      boxShadow: `0 2px 10px ${C.secondary}30`,
                      transition: "all .2s",
                    }}
                    onMouseEnter={(e) =>
                      ((e.currentTarget as HTMLButtonElement).style.transform =
                        "scale(1.06)")
                    }
                    onMouseLeave={(e) =>
                      ((e.currentTarget as HTMLButtonElement).style.transform =
                        "scale(1)")
                    }
                  >
                    <Plus size={18} />
                  </button>
                  <div style={{ flex: 1, position: "relative" }}>
                    <Search
                      size={13}
                      color="#94a3b8"
                      style={{
                        position: "absolute",
                        left: "10px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        pointerEvents: "none",
                      }}
                    />
                    <input
                      type="search"
                      aria-label="Buscar clientes"
                      placeholder="Buscar por nombre, correo, CI/NIT o teléfono"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="ncp-input"
                      style={{
                        width: "100%",
                        padding: "9px 9px 9px 30px",
                        minHeight: "40px",
                        borderRadius: "9px",
                        border: `1px solid ${C.line}`,
                        background: "#fff",
                        fontSize: ".8rem",
                        color: "#334155",
                      }}
                    />
                  </div>
                </div>

                {clientesLoading && (
                  <LoadingLine>Cargando clientes…</LoadingLine>
                )}
                {clientesError && (
                  <ErrorBox
                    message={clientesError}
                    onRetry={() => setClientesReloadKey((k) => k + 1)}
                  />
                )}
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "6px",
                  }}
                >
                  {clientesLoading ||
                  clientesError ? null : filteredClients.length > 0 ? (
                    filteredClients.map((c) => (
                      <div
                        key={c.id}
                        className={`ncp-client-card ${clienteId === c.id ? "selected" : ""}`}
                        role="group"
                        aria-label={`Cliente ${c.usuario ? `${c.usuario.first_name} ${c.usuario.last_name}` : c.id}`}
                      >
                        <button
                          type="button"
                          aria-pressed={clienteId === c.id}
                          aria-label={`Seleccionar cliente ${c.usuario ? `${c.usuario.first_name} ${c.usuario.last_name}` : c.id}`}
                          onClick={() => {
                            setClienteId(c.id);
                            setUsuarioId(c.usuario?.id ?? null);
                            setActiveTab("summary");
                          }}
                          style={{
                            display: "flex",
                            width: "100%",
                            alignItems: "flex-start",
                            justifyContent: "space-between",
                            marginBottom: "8px",
                            padding: 0,
                            border: 0,
                            background: "transparent",
                            textAlign: "left",
                            cursor: "pointer",
                          }}
                        >
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <p
                              style={{
                                fontSize: ".85rem",
                                fontWeight: "700",
                                color: C.ink,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {c.usuario
                                ? `${c.usuario.first_name} ${c.usuario.last_name}`
                                : `Cliente #${c.id}`}
                            </p>
                            <p
                              style={{
                                fontSize: ".72rem",
                                color: "#94a3b8",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {c.usuario?.email}
                            </p>
                          </div>
                          {clienteId === c.id && (
                            <div
                              style={{
                                width: "20px",
                                height: "20px",
                                borderRadius: "50%",
                                background: "#dff1fb",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexShrink: 0,
                                marginLeft: "8px",
                              }}
                            >
                              <CheckCircle size={12} color={C.secondaryDark} />
                            </div>
                          )}
                        </button>
                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr",
                            gap: "6px",
                          }}
                        >
                          {[
                            ["CI/NIT", c.ci_nit, "ciNit"],
                            ["Teléfono", c.telefono, "telefono"],
                          ].map(([lbl, val, field]) => (
                            <div
                              key={lbl}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                padding: "5px 8px",
                                background: C.canvas,
                                borderRadius: "7px",
                                fontSize: ".72rem",
                              }}
                            >
                              <span style={{ color: C.muted }}>
                                {lbl}:{" "}
                                <span
                                  className="ncp-num"
                                  style={{
                                    color: "#334155",
                                    fontWeight: "600",
                                  }}
                                >
                                  {val || "—"}
                                </span>
                              </span>
                            </div>
                          ))}
                        </div>
                        <button
                          type="button"
                          aria-label={`Editar cliente ${c.usuario?.last_name || c.id}`}
                          onClick={() => {
                            setClienteEditar(c);
                            setShowClientModal(true);
                          }}
                          className="mt-2 inline-flex items-center gap-2 rounded-lg bg-blue-50 px-3 py-2 text-xs font-bold text-[#007FC8]"
                        >
                          <SquarePen size={14} />
                          Editar cliente
                        </button>
                      </div>
                    ))
                  ) : (
                    <div style={{ textAlign: "center", padding: "32px 16px" }}>
                      <Users
                        size={36}
                        color="#d1d5db"
                        style={{ margin: "0 auto 10px" }}
                      />
                      <p
                        style={{
                          fontSize: ".85rem",
                          color: "#94a3b8",
                          fontWeight: "500",
                        }}
                      >
                        No se encontraron clientes
                      </p>
                    </div>
                  )}
                </div>
              </div>
            }
          </aside>
        )}
        {/* COLUMNA CENTRAL — Reserva */}
        <div
          className="ncp-main-panel"
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            margin: "14px 0 14px 14px",
            borderRadius: "14px",
            background: "#fff",
            border: `1px solid ${C.line}`,
            boxShadow: C.shadowMd,
          }}
        >
          <div
            className="ncp-scroll ncp-main-scroll"
            style={{ flex: 1, overflowY: "auto", padding: "14px 18px 18px" }}
          >
            {/* El mismo renglón contiene categorías en el primer plano
                            y Volver en el segundo. */}
            {showSelectionPlane ? (
              <div
                role="group"
                aria-label="Filtrar servicios por categoría"
                className="ncp-filter-bar"
              >
                <button
                  type="button"
                  className="ncp-chip"
                  aria-pressed={categoryFilter === ALL_FILTER}
                  onClick={() => handleSelectFilter(ALL_FILTER)}
                >
                  <LayoutGrid size={13} />
                  Todos los espacios
                </button>
                {currentCategories.map((cat) => (
                  <button
                    type="button"
                    key={cat.id}
                    className="ncp-chip"
                    aria-pressed={categoryFilter === cat.id}
                    onClick={() => handleSelectFilter(cat.id)}
                  >
                    {cat.nombre}
                  </button>
                ))}
              </div>
            ) : (
              <div className="ncp-filter-bar">
                <button
                  type="button"
                  className="ncp-chip ncp-back-btn"
                  aria-label="Volver a servicios y planes"
                  onClick={() => {
                    setStep(STEP_PLAN);
                    setSelectedSpace(null);
                    setRange(undefined);
                    setHoraInicio(null);
                    setHoraFin(null);
                    setEspaciosDisponibles({});
                  }}
                >
                  <ArrowLeft size={14} /> Volver
                </button>
              </div>
            )}
            {categoriesLoading && (
              <LoadingLine>Cargando catálogo de la sucursal…</LoadingLine>
            )}
            {categoriesError && (
              <ErrorBox
                message={categoriesError}
                onRetry={() => setCategoriesReloadKey((k) => k + 1)}
              />
            )}
            {planConfigsLoading && !categoriesLoading && (
              <LoadingLine>Cargando configuración de planes…</LoadingLine>
            )}
            {planConfigsError && (
              <ErrorBox
                message={planConfigsError}
                onRetry={() => setPlanConfigsReloadKey((k) => k + 1)}
              />
            )}

            {/* ── PASO 1: Servicios (siempre visibles) ── */}
            {showSelectionPlane && (
              <section
                className={`ncp-section ${sectionState(STEP_SERVICE)}`}
                aria-labelledby="ncp-sec-servicio"
              >
                <div className="ncp-section-head">
                  <h2 id="ncp-sec-servicio" className="ncp-section-title">
                    Elige el servicio
                    <span className="ncp-section-sub">{`· ${filterCategory ? filterCategory.nombre : "Todos los espacios"}`}</span>
                  </h2>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    {!servicesLoading && !servicesError && (
                      <span className="ncp-section-sub ncp-num">{`${services.length} ${services.length === 1 ? "servicio" : "servicios"}`}</span>
                    )}
                  </div>
                </div>
                {servicesLoading && (
                  <LoadingLine>Cargando servicios…</LoadingLine>
                )}
                {servicesError && (
                  <ErrorBox
                    message={servicesError}
                    onRetry={() => setServicesReloadKey((k) => k + 1)}
                  />
                )}
                {!servicesLoading &&
                  !servicesError &&
                  !categoriesLoading &&
                  !categoriesError &&
                  services.length === 0 && (
                    <EmptyNote>
                      No hay servicios activos para este filtro en la sucursal.
                    </EmptyNote>
                  )}
                {services.length > 0 && (
                  <div className="ncp-service-grid">
                    {services.map((srv) => {
                      const isActive = selectedService?.id === srv.id;
                      const catName =
                        categoryFilter === ALL_FILTER
                          ? currentCategories.find(
                              (c) => c.id === srv.categoria,
                            )?.nombre
                          : undefined;
                      return (
                        <button
                          type="button"
                          key={srv.id}
                          className="ncp-service ncp-service-card"
                          aria-pressed={isActive}
                          aria-label={`Seleccionar servicio ${srv.nombre}`}
                          onClick={() => handleSelectService(srv)}
                        >
                          {srv.imagen ? (
                            <img
                              src={srv.imagen}
                              alt={srv.nombre}
                              className="ncp-service-image"
                            />
                          ) : (
                            <span
                              aria-hidden="true"
                              className="ncp-service-image-placeholder"
                            >
                              <Package
                                size={34}
                                color={isActive ? C.secondaryDark : C.navy}
                              />
                            </span>
                          )}
                          <span className="ncp-service-body">
                            {catName && (
                              <span
                                className="ncp-service-meta"
                                style={{ display: "block" }}
                              >{`Categoría: ${catName}`}</span>
                            )}
                            <span
                              className="ncp-service-name"
                              style={{ display: "block" }}
                            >
                              {srv.nombre}
                            </span>
                            {srv.descripcion && (
                              <span className="ncp-service-desc">
                                {srv.descripcion}
                              </span>
                            )}
                            {srv.requiere_espacio && (
                              <span className="ncp-tag">
                                <MapPin size={10} />
                                Requiere espacio
                              </span>
                            )}
                            {isActive && (
                              <CheckCircle
                                size={18}
                                color={C.secondary}
                                aria-hidden="true"
                                style={{
                                  position: "absolute",
                                  right: "12px",
                                  top: "13px",
                                }}
                              />
                            )}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </section>
            )}

            {/* ── PASO 2: Planes ── */}
            {showPlanSection && selectedService && (
              <section
                className={`ncp-section ${sectionState(STEP_PLAN)}`}
                aria-labelledby="ncp-sec-plan"
              >
                <div className="ncp-section-head">
                  <h2 id="ncp-sec-plan" className="ncp-section-title">
                    Elige el plan
                    <span className="ncp-section-sub">{`· ${selectedService.nombre}`}</span>
                  </h2>
                </div>
                {plansLoading && (
                  <LoadingLine>Cargando planes del servicio…</LoadingLine>
                )}
                {plansError && (
                  <ErrorBox
                    message={plansError}
                    onRetry={() => setPlansReloadKey((k) => k + 1)}
                  />
                )}
                {!plansLoading && !plansError && pricePlans.length === 0 && (
                  <EmptyNote>Sin planes activos para este servicio.</EmptyNote>
                )}
                {!plansLoading && !plansError && pricePlans.length > 0 && (
                  <div className="ncp-selection-grid">
                    {pricePlans.map((plan) => {
                      const cfg = planConfigs.find(
                        (c) => c.id === plan.plan_duracion_config,
                      );
                      const isTimer = cfg?.usa_temporizador || false;
                      const isActive = selectedPlan?.id === plan.id;
                      return (
                        <div
                          key={plan.id}
                          className={`ncp-plan-card${isActive ? " active" : ""}`}
                          role="button"
                          tabIndex={0}
                          aria-pressed={isActive}
                          onClick={() => handleSelectPlan(plan)}
                          onKeyDown={(event) =>
                            activateWithKeyboard(event, () =>
                              handleSelectPlan(plan),
                            )
                          }
                        >
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "flex-start",
                              gap: "8px",
                              marginBottom: "10px",
                            }}
                          >
                            <div style={{ minWidth: 0 }}>
                              <p
                                style={{
                                  fontSize: ".9rem",
                                  fontWeight: "800",
                                  color: C.ink,
                                  marginBottom: "2px",
                                }}
                              >
                                {plan.plan_nombre}
                              </p>
                              {plan.duracion_inicial && (
                                <p
                                  style={{
                                    fontSize: ".7rem",
                                    color: "#94a3b8",
                                  }}
                                  className="ncp-num"
                                >
                                  {plan.duracion_inicial}{" "}
                                  {plan.duracion_inicial === 1
                                    ? "hora inicial"
                                    : "horas iniciales"}
                                </p>
                              )}
                              {isTimer && (
                                <span
                                  style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: "3px",
                                    marginTop: "5px",
                                    padding: "2px 7px",
                                    borderRadius: "5px",
                                    background: `${C.accentDark}12`,
                                    color: C.accentDark,
                                    fontSize: ".63rem",
                                    fontWeight: "700",
                                  }}
                                >
                                  <Timer size={10} /> Temporizador
                                </span>
                              )}
                            </div>
                            <div
                              className="ncp-num"
                              style={{
                                background: isActive ? "#dff1fb" : "#e8f3fb",
                                borderRadius: "9px",
                                padding: "5px 10px",
                                transition: "background .2s",
                                flexShrink: 0,
                              }}
                            >
                              <span
                                style={{
                                  fontSize: "1.02rem",
                                  fontWeight: "900",
                                  color: C.secondaryDark,
                                }}
                              >
                                {plan.precio_inicial}
                              </span>
                              <span
                                style={{
                                  fontSize: ".68rem",
                                  color: C.secondaryDark,
                                  marginLeft: "2px",
                                }}
                              >
                                Bs.
                              </span>
                            </div>
                          </div>
                          {plan.precio_posterior != null && (
                            <div
                              style={{
                                padding: "6px 9px",
                                background: C.canvas,
                                borderRadius: "8px",
                                marginBottom: "10px",
                              }}
                            >
                              <p
                                className="ncp-num"
                                style={{ fontSize: ".72rem", color: C.muted }}
                              >
                                <span style={{ fontWeight: "700" }}>
                                  Posterior:
                                </span>{" "}
                                {plan.precio_posterior} Bs.
                              </p>
                            </div>
                          )}
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                            }}
                          >
                            <span
                              style={{
                                fontSize: ".74rem",
                                fontWeight: "700",
                                color: isActive ? C.secondaryDark : "#94a3b8",
                              }}
                            >
                              {isActive ? "✓ Seleccionado" : "Seleccionar"}
                            </span>
                            <div
                              style={{
                                width: "24px",
                                height: "24px",
                                borderRadius: "50%",
                                background: isActive
                                  ? "#dff1fb"
                                  : `${C.secondary}12`,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                transition: "background .2s",
                              }}
                            >
                              <ChevronRight
                                size={13}
                                color={isActive ? C.secondaryDark : C.secondary}
                              />
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </section>
            )}

            {/* ── PASO 3: Espacios ── */}
            {showSpaceSection && (
              <section
                className={`ncp-section ${sectionState(STEP_SPACE)}`}
                aria-labelledby="ncp-sec-espacio"
              >
                <div className="ncp-section-head">
                  <h2 id="ncp-sec-espacio" className="ncp-section-title">
                    Elige el espacio
                  </h2>
                </div>
                {availabilityError && (
                  <ErrorBox
                    message={availabilityError}
                    onRetry={() => void verificarDisponibilidadEspacios()}
                  />
                )}
                {spacesLoading && (
                  <LoadingLine>Cargando espacios del servicio…</LoadingLine>
                )}
                {spacesError && (
                  <ErrorBox
                    message={spacesError}
                    onRetry={() => setSpacesReloadKey((k) => k + 1)}
                  />
                )}
                {citasLoading && (
                  <LoadingLine>
                    Cargando reservas existentes de la sucursal…
                  </LoadingLine>
                )}
                {citasError && (
                  <ErrorBox
                    message={citasError}
                    onRetry={() => setCitasReloadKey((k) => k + 1)}
                  />
                )}
                {!spacesLoading && !spacesError && spaces.length === 0 && (
                  <EmptyNote>
                    Sin espacios activos para este servicio en la sucursal.
                  </EmptyNote>
                )}
                <div
                  className="ncp-selection-grid"
                  style={{
                    gridTemplateColumns: "repeat(auto-fill,minmax(220px,1fr))",
                  }}
                >
                  {!spacesLoading &&
                    !spacesError &&
                    spaces.map((esp) => {
                      const disponibilidad = espaciosDisponibles[esp.id];
                      const estaDisp = disponibilidad === true;
                      const rangoCompleto =
                        !!fechaInicio && !!fechaFin && !!horaInicio;
                      const citasEsp = currentCitas.filter(
                        (c) =>
                          c.espacio_servicio === esp.id &&
                          (c.estado === "Confirmada" ||
                            c.estado === "Pendiente"),
                      );
                      const puede = !rangoCompleto || estaDisp;
                      const primeraCita = [...citasEsp].sort(
                        (a, b) =>
                          new Date(
                            a.fecha_inicio + "T" + a.hora_inicio,
                          ).getTime() -
                          new Date(
                            b.fecha_inicio + "T" + b.hora_inicio,
                          ).getTime(),
                      )[0];
                      const isSelected = selectedSpace?.id === esp.id;
                      return (
                        <div
                          key={esp.id}
                          className={`ncp-sel-card${isSelected ? " active" : ""}${!puede ? " unavailable" : ""}`}
                          role="group"
                          aria-label={`Espacio ${esp.espacio_nombre}`}
                        >
                          {esp.espacio_imagen && (
                            <div
                              style={{
                                height: "92px",
                                overflow: "hidden",
                                position: "relative",
                              }}
                            >
                              <img
                                src={esp.espacio_imagen}
                                alt={esp.espacio_nombre}
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "cover",
                                }}
                              />
                              <div
                                style={{
                                  position: "absolute",
                                  top: "8px",
                                  right: "8px",
                                  padding: "3px 9px",
                                  borderRadius: "6px",
                                  fontSize: ".66rem",
                                  fontWeight: "700",
                                  backdropFilter: "blur(6px)",
                                  background: estaDisp
                                    ? `${C.secondaryLight}dd`
                                    : `${C.primary}dd`,
                                  color: "#fff",
                                }}
                              >
                                {!rangoCompleto
                                  ? "Seleccionar"
                                  : estaDisp
                                    ? "Disponible"
                                    : disponibilidad === false
                                      ? "Ocupado"
                                      : availabilityError
                                        ? "Sin verificar"
                                        : "Verificando"}
                              </div>
                            </div>
                          )}
                          <div style={{ padding: "11px 12px" }}>
                            <p
                              style={{
                                fontSize: ".86rem",
                                fontWeight: "800",
                                color: C.ink,
                                marginBottom: "5px",
                              }}
                            >
                              {esp.espacio_nombre}
                            </p>
                            {esp.cantidad_personas && (
                              <div
                                className="ncp-num"
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "5px",
                                  fontSize: ".72rem",
                                  color: C.muted,
                                  marginBottom: "6px",
                                }}
                              >
                                <Users size={12} /> Capacidad:{" "}
                                {esp.cantidad_personas} personas
                              </div>
                            )}
                            {primeraCita && (
                              <div
                                style={{
                                  padding: "7px 9px",
                                  background: `${C.accentDark}08`,
                                  border: `1px solid ${C.accentDark}20`,
                                  borderRadius: "8px",
                                  marginBottom: "8px",
                                }}
                              >
                                <p
                                  style={{
                                    fontSize: ".66rem",
                                    fontWeight: "700",
                                    color: C.accentDark,
                                    marginBottom: "2px",
                                  }}
                                >
                                  Próxima reserva
                                </p>
                                <p
                                  style={{ fontSize: ".7rem", color: C.muted }}
                                >
                                  {(() => {
                                    const cl = clientes.find(
                                      (c) =>
                                        c.usuario?.id === primeraCita.cliente,
                                    );
                                    return cl?.usuario
                                      ? `${cl.usuario.first_name} ${cl.usuario.last_name}`
                                      : `Cliente #${primeraCita.cliente}`;
                                  })()}
                                </p>
                              </div>
                            )}
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: "6px",
                                flexWrap: "wrap",
                              }}
                            >
                              <button
                                type="button"
                                disabled={!puede}
                                aria-pressed={isSelected}
                                aria-label={`Seleccionar espacio ${esp.espacio_nombre}`}
                                onClick={() => handleSelectSpace(esp)}
                                style={{
                                  minHeight: 40,
                                  padding: "6px 10px",
                                  borderRadius: 8,
                                  border: `1px solid ${puede ? C.secondary : "#cbd5e1"}`,
                                  background: isSelected
                                    ? `${C.secondary}12`
                                    : "#fff",
                                  fontSize: ".74rem",
                                  fontWeight: "700",
                                  cursor: puede ? "pointer" : "not-allowed",
                                  color: puede
                                    ? isSelected
                                      ? C.secondary
                                      : "#475569"
                                    : C.primary,
                                }}
                              >
                                {puede
                                  ? isSelected
                                    ? "✓ Seleccionado"
                                    : "Seleccionar"
                                  : "No disponible"}
                              </button>
                              {citasEsp.length > 0 && (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(
                                      `/point-of-sale/ver-reservas/${esp.id}`,
                                      {
                                        state: {
                                          sesionId: sesionActiva?.id ?? null,
                                        },
                                      },
                                    );
                                  }}
                                  style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: "3px",
                                    fontSize: ".7rem",
                                    fontWeight: "700",
                                    color: C.secondary,
                                    background: "none",
                                    border: "none",
                                    cursor: "pointer",
                                    padding: "6px 8px",
                                    minHeight: 36,
                                    borderRadius: "6px",
                                    transition: "background .2s",
                                  }}
                                  onMouseEnter={(e) =>
                                    ((
                                      e.currentTarget as HTMLButtonElement
                                    ).style.background = `${C.secondary}10`)
                                  }
                                  onMouseLeave={(e) =>
                                    ((
                                      e.currentTarget as HTMLButtonElement
                                    ).style.background = "transparent")
                                  }
                                >
                                  <Eye size={11} /> Ver reservas
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                </div>
              </section>
            )}

            {/* ── PASO 4: Fechas y Horas ── */}
            {showDatesSection && (
              <section
                className={`ncp-section ${sectionState(STEP_DATES)}`}
                aria-labelledby="ncp-sec-fechas"
              >
                <div className="ncp-section-head">
                  <h2 id="ncp-sec-fechas" className="ncp-section-title">
                    Fechas y horario
                  </h2>
                </div>

                {/* Tipo de días */}
                <div
                  style={{
                    marginBottom: "14px",
                    padding: "10px 14px",
                    background: C.canvas,
                    borderRadius: "10px",
                    border: `1px solid ${C.line}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: "12px",
                    flexWrap: "wrap",
                  }}
                >
                  <div>
                    <p
                      style={{
                        fontSize: ".8rem",
                        fontWeight: "800",
                        color: C.ink,
                        marginBottom: "2px",
                      }}
                    >
                      Tipo de días
                    </p>
                    <p style={{ fontSize: ".7rem", color: "#94a3b8" }}>
                      ¿Incluir fines de semana o solo días hábiles?
                    </p>
                  </div>
                  <div
                    style={{
                      position: "relative",
                      flexShrink: 0,
                      minWidth: "170px",
                    }}
                  >
                    <Clock
                      size={14}
                      color={C.secondary}
                      aria-hidden="true"
                      style={{
                        position: "absolute",
                        left: "12px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        pointerEvents: "none",
                      }}
                    />
                    <select
                      aria-label="Tipo de días"
                      value={daySelectionType}
                      onChange={(event) =>
                        setDaySelectionType(
                          event.target.value as "all" | "business",
                        )
                      }
                      style={{
                        width: "100%",
                        padding: "8px 34px 8px 34px",
                        minHeight: "38px",
                        borderRadius: "9px",
                        border: `1px solid ${C.line}`,
                        background: "#fff",
                        fontSize: ".78rem",
                        fontWeight: "700",
                        color: "#334155",
                        cursor: "pointer",
                      }}
                    >
                      <option value="all">Todos los días</option>
                      <option value="business">Días hábiles</option>
                    </select>
                  </div>
                </div>

                <div
                  className="ncp-date-grid"
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "14px",
                  }}
                >
                  {/* Calendario */}
                  <div
                    style={{
                      background: "#fff",
                      borderRadius: "12px",
                      border: `1px solid ${C.line}`,
                      padding: "14px",
                      boxShadow: C.shadowSm,
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: "12px",
                        marginBottom: "10px",
                        flexWrap: "wrap",
                      }}
                    >
                      <div>
                        <p
                          style={{
                            fontSize: ".66rem",
                            fontWeight: "800",
                            color: C.secondary,
                            textTransform: "uppercase",
                            letterSpacing: ".08em",
                          }}
                        >
                          Disponibilidad del espacio
                        </p>
                        <p
                          style={{
                            fontSize: ".9rem",
                            fontWeight: "900",
                            color: C.navy,
                            marginTop: "2px",
                          }}
                        >
                          {selectedSpace?.espacio_nombre ||
                            "Sin espacio requerido"}
                        </p>
                      </div>
                    </div>
                    {monthlyOccupancy.loading && (
                      <p
                        role="status"
                        style={{
                          fontSize: ".72rem",
                          color: C.muted,
                          marginBottom: "8px",
                        }}
                      >
                        Consultando agenda del mes…
                      </p>
                    )}
                    {monthlyOccupancy.error && (
                      <p
                        role="alert"
                        style={{
                          fontSize: ".72rem",
                          color: "#b91c1c",
                          marginBottom: "8px",
                        }}
                      >
                        {monthlyOccupancy.error}
                      </p>
                    )}
                    <DayPicker
                      mode="range"
                      selected={range}
                      month={parseLocalDate(`${calendarMonth}-01`)}
                      onSelect={(dr) => {
                        setRange(dr);
                        if (!dr?.from) {
                          setHoraInicio(null);
                          setHoraFin(null);
                        }
                      }}
                      onMonthChange={(month) =>
                        setCalendarMonth(format(month, "yyyy-MM"))
                      }
                      modifiers={{
                        occupied: Array.from(
                          monthlyOccupancy.occupiedDates,
                        ).map(parseLocalDate),
                      }}
                      modifiersClassNames={{ occupied: "ncp-day-occupied" }}
                      labels={{
                        labelDayButton: (date, modifiers) =>
                          `${format(date, "d 'de' MMMM 'de' yyyy", { locale: es })}${modifiers.occupied ? ", con ocupación" : ""}`,
                      }}
                      disabled={
                        daySelectionType === "business"
                          ? [
                              { before: startOfDay(new Date()) },
                              { dayOfWeek: [0, 6] },
                            ]
                          : { before: startOfDay(new Date()) }
                      }
                      numberOfMonths={1}
                      locale={es}
                    />
                    {selectedSpace && (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "7px",
                          marginTop: "12px",
                          paddingTop: "10px",
                          borderTop: `1px solid ${C.line}`,
                          flexWrap: "wrap",
                        }}
                      >
                        <span
                          aria-hidden="true"
                          style={{
                            width: "9px",
                            height: "9px",
                            borderRadius: "50%",
                            background: bookingCalendarTokens.occupiedDot,
                          }}
                        />
                        <span
                          style={{
                            fontSize: ".7rem",
                            fontWeight: "800",
                            color: "#9a3412",
                          }}
                        >
                          Con ocupación
                        </span>
                        <span style={{ fontSize: ".68rem", color: C.muted }}>
                          Puedes seleccionarla para revisar horarios libres.
                        </span>
                      </div>
                    )}
                    {selectedSpaceHasConflict && (
                      <p
                        role="alert"
                        style={{
                          marginTop: "12px",
                          padding: "10px 12px",
                          borderRadius: "10px",
                          background: "#fef2f2",
                          border: "1px solid #fecaca",
                          fontSize: ".72rem",
                          fontWeight: "700",
                          color: "#b91c1c",
                        }}
                      >
                        El espacio ya está ocupado dentro del plazo y horario
                        seleccionados. Ajusta la fecha o la hora; el espacio se
                        mantendrá seleccionado.
                      </p>
                    )}
                    {fechaInicio &&
                      monthlyOccupancy.daysByDate[fechaInicio]?.detalles && (
                        <div
                          style={{
                            marginTop: "12px",
                            padding: "12px",
                            borderRadius: "10px",
                            background: C.canvas,
                            border: `1px solid ${C.line}`,
                          }}
                        >
                          <p
                            style={{
                              fontSize: ".72rem",
                              fontWeight: "900",
                              color: "#334155",
                              marginBottom: "7px",
                            }}
                          >
                            Agenda del día seleccionado
                          </p>
                          <p
                            style={{
                              fontSize: ".7rem",
                              color: C.muted,
                              marginBottom: "7px",
                            }}
                          >
                            Intervalos ocupados del día; otros horarios pueden
                            estar disponibles.
                          </p>
                          {monthlyOccupancy.daysByDate[
                            fechaInicio
                          ].detalles!.map((item, index) => (
                            <div
                              key={`${item.tipo}-${item.id}-${index}`}
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                gap: "10px",
                                fontSize: ".72rem",
                                color: "#475569",
                                padding: "4px 0",
                              }}
                            >
                              <span>
                                {item.tipo === "cita"
                                  ? `Cita ${item.codigo || `#${item.id}`}`
                                  : "Horas regalo"}
                                {item.cliente ? ` · ${item.cliente}` : ""}
                              </span>
                              <strong
                                className="ncp-num"
                                style={{ color: C.ink }}
                              >
                                {item.hora_inicio}–{item.hora_fin}
                              </strong>
                            </div>
                          ))}
                        </div>
                      )}
                  </div>

                  {/* Controles de hora */}
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "12px",
                    }}
                  >
                    {/* Fechas display */}
                    <div
                      className="ncp-date-fields"
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: "10px",
                      }}
                    >
                      {[
                        ["Fecha inicio", fechaInicio],
                        ["Fecha fin", fechaFin],
                      ].map(([lbl, val]) => (
                        <div key={lbl}>
                          <label
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                              fontSize: ".66rem",
                              fontWeight: "800",
                              color: "#94a3b8",
                              textTransform: "uppercase",
                              letterSpacing: ".06em",
                              marginBottom: "5px",
                            }}
                          >
                            <Calendar size={10} /> {lbl}
                          </label>
                          <div
                            className="ncp-num"
                            style={{
                              padding: "9px 12px",
                              minHeight: "40px",
                              borderRadius: "9px",
                              border: `1px solid ${C.line}`,
                              background: C.canvas,
                              fontSize: ".8rem",
                              color: val ? "#334155" : "#cbd5e1",
                              fontWeight: val ? "600" : "400",
                            }}
                          >
                            {val ? fmtDateSpanish(val) : "Seleccione"}
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Horas */}
                    <div
                      className="ncp-date-fields"
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: "10px",
                      }}
                    >
                      <div>
                        <label
                          htmlFor="ncp-hora-inicio"
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                            fontSize: ".66rem",
                            fontWeight: "800",
                            color: "#94a3b8",
                            textTransform: "uppercase",
                            letterSpacing: ".06em",
                            marginBottom: "5px",
                          }}
                        >
                          <Clock size={10} /> Hora inicio
                        </label>
                        <input
                          id="ncp-hora-inicio"
                          type="time"
                          className="ncp-input ncp-num"
                          value={horaInicio || ""}
                          onChange={(e) =>
                            setHoraInicio(e.target.value || null)
                          }
                          required
                          style={{
                            width: "100%",
                            padding: "9px 12px",
                            minHeight: "40px",
                            borderRadius: "9px",
                            border: `1px solid ${C.line}`,
                            background: "#fff",
                            fontSize: ".85rem",
                            color: "#334155",
                          }}
                        />
                      </div>
                      <div>
                        <label
                          htmlFor="ncp-hora-fin"
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                            fontSize: ".66rem",
                            fontWeight: "800",
                            color: "#94a3b8",
                            textTransform: "uppercase",
                            letterSpacing: ".06em",
                            marginBottom: "5px",
                          }}
                        >
                          <Clock size={10} /> Hora fin
                        </label>
                        <input
                          id="ncp-hora-fin"
                          type="time"
                          className="ncp-input ncp-num"
                          value={usaTemporizador ? "" : horaFin || ""}
                          disabled={usaTemporizador}
                          onChange={(e) => {
                            if (!usaTemporizador) {
                              setHoraFin(e.target.value || null);
                              setIsHoraFinManuallyModified(true);
                            }
                          }}
                          style={{
                            width: "100%",
                            padding: "9px 12px",
                            minHeight: "40px",
                            borderRadius: "9px",
                            border: `1px solid ${C.line}`,
                            background: usaTemporizador ? "#f1f5f9" : "#fff",
                            fontSize: ".85rem",
                            color: usaTemporizador ? "#94a3b8" : "#334155",
                            cursor: usaTemporizador ? "not-allowed" : "auto",
                          }}
                        />
                        {usaTemporizador && (
                          <p
                            style={{
                              fontSize: ".68rem",
                              color: C.accentDark,
                              marginTop: "4px",
                              display: "flex",
                              alignItems: "center",
                              gap: "3px",
                            }}
                          >
                            <Timer size={10} />
                            Determinado por temporizador
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Cantidad periodos */}
                    <div>
                      <label
                        htmlFor="ncp-periodos"
                        style={{
                          display: "block",
                          fontSize: ".66rem",
                          fontWeight: "800",
                          color: "#94a3b8",
                          textTransform: "uppercase",
                          letterSpacing: ".06em",
                          marginBottom: "5px",
                        }}
                      >
                        Cantidad de {selectedPlan?.plan_nombre || "Periodos"}
                      </label>
                      <div style={{ position: "relative" }}>
                        <input
                          id="ncp-periodos"
                          type="number"
                          min="1"
                          step="1"
                          className="ncp-input ncp-num"
                          value={cantidadPeriodos || ""}
                          onChange={(e) =>
                            setCantidadPeriodos(
                              e.target.value === ""
                                ? null
                                : Number(e.target.value),
                            )
                          }
                          required
                          style={{
                            width: "100%",
                            padding: "9px 60px 9px 12px",
                            minHeight: "40px",
                            borderRadius: "9px",
                            border: `1px solid ${C.line}`,
                            background: "#fff",
                            fontSize: ".85rem",
                            color: "#334155",
                          }}
                        />
                        <span
                          style={{
                            position: "absolute",
                            right: "12px",
                            top: "50%",
                            transform: "translateY(-50%)",
                            fontSize: ".7rem",
                            color: "#94a3b8",
                            fontWeight: "600",
                            pointerEvents: "none",
                          }}
                        >
                          periodos
                        </span>
                      </div>
                    </div>

                    {/* Resumen selección */}
                    {fechaInicio && horaInicio && cantidadPeriodos && (
                      <div
                        style={{
                          padding: "10px 12px",
                          background: `${C.secondaryLight}0a`,
                          border: `1px solid ${C.secondaryLight}40`,
                          borderRadius: "10px",
                        }}
                      >
                        <p
                          style={{
                            fontSize: ".66rem",
                            fontWeight: "800",
                            color: "#065f46",
                            textTransform: "uppercase",
                            letterSpacing: ".06em",
                            marginBottom: "5px",
                          }}
                        >
                          Resumen selección
                        </p>
                        <p
                          className="ncp-num"
                          style={{
                            fontSize: ".8rem",
                            color: "#334155",
                            fontWeight: "600",
                          }}
                        >
                          {fmtDateSpanish(fechaInicio)} {horaInicio}
                          {fechaFin && fechaFin !== fechaInicio
                            ? ` → ${fmtDateSpanish(fechaFin)}`
                            : ""}{" "}
                          {usaTemporizador
                            ? "· Salida al finalizar"
                            : horaFin
                              ? `- ${horaFin}`
                              : ""}
                        </p>
                        {!usaTemporizador &&
                          isOvernightRange(horaInicio, horaFin) && (
                            <p
                              style={{
                                fontSize: ".7rem",
                                color: C.accentDark,
                                fontWeight: "700",
                                marginTop: "3px",
                                display: "flex",
                                alignItems: "center",
                                gap: "3px",
                              }}
                            >
                              <Clock size={10} />
                              Termina al día siguiente (
                              {dailyHours(
                                range?.from ?? new Date(),
                                horaInicio!,
                                horaFin!,
                              )}{" "}
                              h por día)
                            </p>
                          )}
                        <p
                          className="ncp-num"
                          style={{
                            fontSize: ".72rem",
                            color: C.muted,
                            marginTop: "2px",
                          }}
                        >
                          {cantidadPeriodos} periodo
                          {cantidadPeriodos > 1 ? "s" : ""} ·{" "}
                          {daySelectionType === "all"
                            ? "Todos los días"
                            : "Días hábiles"}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </section>
            )}
          </div>
        </div>

        {/* COLUMNA DERECHA — Clientes / Resumen */}
        <div
          className="ncp-customer-panel"
          style={{
            width: "400px",
            display: "flex",
            flexDirection: "column",
            margin: "14px",
            borderRadius: "14px",
            background: "#fff",
            border: `1px solid ${C.line}`,
            boxShadow: C.shadowMd,
            overflow: "hidden",
            flexShrink: 0,
          }}
        >
          {/* Tabs */}
          <div
            style={{
              display: "flex",
              borderBottom: `1px solid ${C.line}`,
              flexShrink: 0,
            }}
            role="group"
            aria-label="Cliente y resumen"
          >
            <button
              type="button"
              aria-pressed={activeTab === "clients"}
              className={`ncp-tab ${activeTab === "clients" ? "active" : "inactive"}`}
              onClick={() => setActiveTab("clients")}
            >
              <Users size={15} /> Clientes
            </button>
            <button
              type="button"
              aria-pressed={activeTab === "summary"}
              className={`ncp-tab ${activeTab === "summary" ? "active" : "inactive"}`}
              onClick={() => setActiveTab("summary")}
            >
              <CreditCard size={15} /> Resumen
            </button>
          </div>

          {/* Contenido tabs */}
          <div className="ncp-scroll" style={{ flex: 1, overflowY: "auto" }}>
            {/* ── TAB CLIENTES ── */}
            {!onReservaCreada && activeTab === "clients" && (
              <div style={{ padding: "14px" }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 8,
                    marginBottom: 10,
                  }}
                >
                  <div>
                    <p
                      style={{
                        fontSize: ".82rem",
                        fontWeight: 900,
                        color: C.navy,
                      }}
                    >
                      Directorio de clientes
                    </p>
                    <p
                      className="ncp-num"
                      style={{ fontSize: ".7rem", color: C.muted }}
                    >
                      {filteredClients.length} cliente
                      {filteredClients.length === 1 ? "" : "s"} disponible
                      {filteredClients.length === 1 ? "" : "s"}
                    </p>
                  </div>
                  {searchTerm && (
                    <button
                      type="button"
                      onClick={() => setSearchTerm("")}
                      style={{
                        border: 0,
                        background: "transparent",
                        fontSize: ".7rem",
                        fontWeight: 800,
                        color: C.secondary,
                        cursor: "pointer",
                        minHeight: 32,
                      }}
                    >
                      Limpiar
                    </button>
                  )}
                </div>
                <div
                  style={{ display: "flex", gap: "8px", marginBottom: "12px" }}
                >
                  <button
                    type="button"
                    onClick={handleOpenClientModal}
                    title="Nuevo Cliente"
                    aria-label="Nuevo cliente"
                    style={{
                      width: "40px",
                      height: "40px",
                      borderRadius: "10px",
                      border: "none",
                      background: `linear-gradient(135deg,${C.secondary},${C.secondaryDark})`,
                      color: "#fff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      flexShrink: 0,
                      boxShadow: `0 2px 10px ${C.secondary}30`,
                      transition: "all .2s",
                    }}
                    onMouseEnter={(e) =>
                      ((e.currentTarget as HTMLButtonElement).style.transform =
                        "scale(1.06)")
                    }
                    onMouseLeave={(e) =>
                      ((e.currentTarget as HTMLButtonElement).style.transform =
                        "scale(1)")
                    }
                  >
                    <Plus size={18} />
                  </button>
                  <div style={{ flex: 1, position: "relative" }}>
                    <Search
                      size={13}
                      color="#94a3b8"
                      style={{
                        position: "absolute",
                        left: "10px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        pointerEvents: "none",
                      }}
                    />
                    <input
                      type="search"
                      aria-label="Buscar clientes"
                      placeholder="Buscar por nombre, correo, CI/NIT o teléfono"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="ncp-input"
                      style={{
                        width: "100%",
                        padding: "9px 9px 9px 30px",
                        minHeight: "40px",
                        borderRadius: "9px",
                        border: `1px solid ${C.line}`,
                        background: "#fff",
                        fontSize: ".8rem",
                        color: "#334155",
                      }}
                    />
                  </div>
                </div>

                {clientesLoading && (
                  <LoadingLine>Cargando clientes…</LoadingLine>
                )}
                {clientesError && (
                  <ErrorBox
                    message={clientesError}
                    onRetry={() => setClientesReloadKey((k) => k + 1)}
                  />
                )}
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "6px",
                  }}
                >
                  {clientesLoading ||
                  clientesError ? null : filteredClients.length > 0 ? (
                    filteredClients.map((c) => (
                      <div
                        key={c.id}
                        className={`ncp-client-card ${clienteId === c.id ? "selected" : ""}`}
                        role="group"
                        aria-label={`Cliente ${c.usuario ? `${c.usuario.first_name} ${c.usuario.last_name}` : c.id}`}
                      >
                        <button
                          type="button"
                          aria-pressed={clienteId === c.id}
                          aria-label={`Seleccionar cliente ${c.usuario ? `${c.usuario.first_name} ${c.usuario.last_name}` : c.id}`}
                          onClick={() => {
                            setClienteId(c.id);
                            setUsuarioId(c.usuario?.id ?? null);
                            setActiveTab("summary");
                          }}
                          style={{
                            display: "flex",
                            width: "100%",
                            alignItems: "flex-start",
                            justifyContent: "space-between",
                            marginBottom: "8px",
                            padding: 0,
                            border: 0,
                            background: "transparent",
                            textAlign: "left",
                            cursor: "pointer",
                          }}
                        >
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <p
                              style={{
                                fontSize: ".85rem",
                                fontWeight: "700",
                                color: C.ink,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {c.usuario
                                ? `${c.usuario.first_name} ${c.usuario.last_name}`
                                : `Cliente #${c.id}`}
                            </p>
                            <p
                              style={{
                                fontSize: ".72rem",
                                color: "#94a3b8",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {c.usuario?.email}
                            </p>
                          </div>
                          {clienteId === c.id && (
                            <div
                              style={{
                                width: "20px",
                                height: "20px",
                                borderRadius: "50%",
                                background: "#dff1fb",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexShrink: 0,
                                marginLeft: "8px",
                              }}
                            >
                              <CheckCircle size={12} color={C.secondaryDark} />
                            </div>
                          )}
                        </button>
                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr",
                            gap: "6px",
                          }}
                        >
                          {[
                            ["CI/NIT", c.ci_nit, "ciNit"],
                            ["Teléfono", c.telefono, "telefono"],
                          ].map(([lbl, val, field]) => (
                            <div
                              key={lbl}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                padding: "5px 8px",
                                background: C.canvas,
                                borderRadius: "7px",
                                fontSize: ".72rem",
                              }}
                            >
                              <span style={{ color: C.muted }}>
                                {lbl}:{" "}
                                <span
                                  className="ncp-num"
                                  style={{
                                    color: "#334155",
                                    fontWeight: "600",
                                  }}
                                >
                                  {val || "—"}
                                </span>
                              </span>
                            </div>
                          ))}
                        </div>
                        <button
                          type="button"
                          aria-label={`Editar cliente ${c.usuario?.last_name || c.id}`}
                          onClick={() => {
                            setClienteEditar(c);
                            setShowClientModal(true);
                          }}
                          className="mt-2 inline-flex items-center gap-2 rounded-lg bg-blue-50 px-3 py-2 text-xs font-bold text-[#007FC8]"
                        >
                          <SquarePen size={14} />
                          Editar cliente
                        </button>
                        {/* Badge Declaración Jurada */}
                        <div style={{ marginTop: "8px" }}>
                          {c.declaracion_jurada_firmada ? (
                            <span
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "4px",
                                padding: "3px 8px",
                                borderRadius: "20px",
                                background: "#dcfce7",
                                color: "#166534",
                                fontSize: ".68rem",
                                fontWeight: "700",
                              }}
                            >
                              ✓ Dec. Jurada Firmada
                            </span>
                          ) : (
                            <span
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "4px",
                                padding: "3px 8px",
                                borderRadius: "20px",
                                background: "#fef3c7",
                                color: "#92400e",
                                fontSize: ".68rem",
                                fontWeight: "700",
                              }}
                            >
                              ⚠ Firma Pendiente
                            </span>
                          )}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div style={{ textAlign: "center", padding: "32px 16px" }}>
                      <Users
                        size={36}
                        color="#d1d5db"
                        style={{ margin: "0 auto 10px" }}
                      />
                      <p
                        style={{
                          fontSize: ".85rem",
                          color: "#94a3b8",
                          fontWeight: "500",
                        }}
                      >
                        No se encontraron clientes
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ── TAB RESUMEN ── */}
            {(onReservaCreada || activeTab === "summary") && (
              <div
                style={{
                  padding: "14px",
                  display: "flex",
                  flexDirection: "column",
                  gap: "12px",
                }}
              >
                {/* Cliente seleccionado */}
                {clienteId &&
                  (() => {
                    const cl = clientes.find((c) => c.id === clienteId);
                    return (
                      <div
                        style={{
                          background: `${C.secondary}08`,
                          border: `1px solid ${C.secondary}30`,
                          borderRadius: "12px",
                          padding: "12px",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            marginBottom: "8px",
                          }}
                        >
                          <span
                            style={{
                              fontSize: ".68rem",
                              fontWeight: "800",
                              color: C.secondary,
                              textTransform: "uppercase",
                              letterSpacing: ".06em",
                            }}
                          >
                            Cliente
                          </span>
                          <button
                            type="button"
                            onClick={() => setActiveTab("clients")}
                            style={{
                              fontSize: ".72rem",
                              fontWeight: "700",
                              color: C.secondary,
                              background: "none",
                              border: "none",
                              cursor: "pointer",
                              minHeight: 32,
                            }}
                          >
                            Cambiar
                          </button>
                        </div>
                        <p
                          style={{
                            fontSize: ".92rem",
                            fontWeight: "800",
                            color: C.ink,
                            marginBottom: "8px",
                          }}
                        >
                          {cl?.usuario
                            ? `${cl.usuario.first_name} ${cl.usuario.last_name}`
                            : `Cliente #${clienteId}`}
                        </p>
                        {/* Declaración Jurada status in summary */}
                        {cl?.declaracion_jurada_firmada ? (
                          <div
                            style={{
                              marginBottom: "10px",
                              padding: "8px 10px",
                              borderRadius: "10px",
                              background: "#dcfce7",
                              border: "1px solid #bbf7d0",
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                            }}
                          >
                            <span style={{ fontSize: "1rem" }}>✅</span>
                            <div>
                              <p
                                style={{
                                  fontSize: ".75rem",
                                  fontWeight: "800",
                                  color: "#166534",
                                  margin: 0,
                                }}
                              >
                                Declaración Jurada Acreditada
                              </p>
                              <p
                                style={{
                                  fontSize: ".68rem",
                                  color: "#166534",
                                  margin: 0,
                                  opacity: 0.8,
                                }}
                              >
                                {cl.fecha_declaracion_jurada
                                  ? `Firmada el ${new Date(cl.fecha_declaracion_jurada).toLocaleDateString("es-ES")}`
                                  : "No requiere firmar nuevamente"}
                              </p>
                            </div>
                          </div>
                        ) : (
                          <div
                            style={{
                              marginBottom: "10px",
                              padding: "8px 10px",
                              borderRadius: "10px",
                              background: "#fef3c7",
                              border: "1px solid #fde68a",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              gap: "8px",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                              }}
                            >
                              <span style={{ fontSize: "1rem" }}>⚠️</span>
                              <div>
                                <p
                                  style={{
                                    fontSize: ".75rem",
                                    fontWeight: "800",
                                    color: "#92400e",
                                    margin: 0,
                                  }}
                                >
                                  Declaración Jurada Pendiente
                                </p>
                                <p
                                  style={{
                                    fontSize: ".68rem",
                                    color: "#92400e",
                                    margin: 0,
                                    opacity: 0.8,
                                  }}
                                >
                                  Registrar firma del cliente al atender
                                </p>
                              </div>
                            </div>
                            <button
                              onClick={() => {
                                navigate(`/clients?cliente=${clienteId}`);
                              }}
                              style={{
                                padding: "4px 10px",
                                borderRadius: "8px",
                                background: C.accentDark,
                                color: "#fff",
                                border: "none",
                                fontSize: ".72rem",
                                fontWeight: "800",
                                cursor: "pointer",
                                whiteSpace: "nowrap",
                              }}
                            >
                              Adjuntar Firma
                            </button>
                          </div>
                        )}
                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr",
                            gap: "6px",
                          }}
                        >
                          {[
                            ["CI/NIT", cl?.ci_nit],
                            ["Teléfono", cl?.telefono],
                          ].map(([l, v]) => (
                            <div
                              key={l}
                              style={{
                                background: "rgba(255,255,255,.8)",
                                borderRadius: "8px",
                                padding: "6px 8px",
                              }}
                            >
                              <p
                                style={{
                                  fontSize: ".62rem",
                                  color: "#94a3b8",
                                  fontWeight: "600",
                                  textTransform: "uppercase",
                                  letterSpacing: ".04em",
                                }}
                              >
                                {l}
                              </p>
                              <p
                                className="ncp-num"
                                style={{
                                  fontSize: ".8rem",
                                  fontWeight: "700",
                                  color: "#334155",
                                }}
                              >
                                {v || "—"}
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })()}

                {usuarioId && (
                  <CustomerGiftHoursPanel
                    customerUserId={usuarioId}
                    sucursalId={sucursalId}
                  />
                )}

                {/* Clientes asociados */}
                <div
                  style={{
                    background: C.canvas,
                    border: `1px solid ${C.line}`,
                    borderRadius: "12px",
                    padding: "12px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: "10px",
                    }}
                  >
                    <span
                      style={{
                        fontSize: ".68rem",
                        fontWeight: "800",
                        color: C.muted,
                        textTransform: "uppercase",
                        letterSpacing: ".06em",
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                      }}
                    >
                      <UserPlus size={11} /> Asociados
                    </span>
                    <button
                      type="button"
                      onClick={() => setShowAgregarClientesModal(true)}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "3px",
                        padding: "4px 10px",
                        minHeight: 32,
                        borderRadius: "7px",
                        background: `${C.secondary}10`,
                        color: C.secondary,
                        border: "none",
                        fontSize: ".72rem",
                        fontWeight: "700",
                        cursor: "pointer",
                      }}
                    >
                      <Plus size={11} /> Agregar
                    </button>
                  </div>
                  {clientesAsociadosInfo.length === 0 ? (
                    <p
                      style={{
                        fontSize: ".75rem",
                        color: "#94a3b8",
                        textAlign: "center",
                        padding: "8px 0",
                      }}
                    >
                      Sin clientes adicionales
                    </p>
                  ) : (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "5px",
                      }}
                    >
                      {clientesAsociadosInfo.map((c) => (
                        <div
                          key={c.id}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            padding: "7px 10px",
                            background: "#fff",
                            borderRadius: "8px",
                            border: `1px solid ${C.line}`,
                          }}
                        >
                          <div>
                            <p
                              style={{
                                fontSize: ".78rem",
                                fontWeight: "700",
                                color: "#334155",
                              }}
                            >
                              {c.first_name} {c.last_name}
                            </p>
                            <p
                              style={{
                                fontSize: ".68rem",
                                color: "#94a3b8",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                maxWidth: "200px",
                              }}
                            >
                              {c.email}
                            </p>
                          </div>
                          <button
                            type="button"
                            aria-label={`Quitar a ${c.first_name} ${c.last_name}`}
                            onClick={() => handleRemoverClienteAsociado(c.id)}
                            style={{
                              background: "none",
                              border: "none",
                              cursor: "pointer",
                              padding: "6px",
                              color: C.primary,
                              borderRadius: "5px",
                            }}
                          >
                            <X size={13} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  {selectedSpace?.cantidad_personas && (
                    <div
                      className="ncp-num"
                      style={{
                        marginTop: "8px",
                        padding: "8px 10px",
                        background: `${C.secondary}08`,
                        borderRadius: "8px",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          fontSize: ".75rem",
                          color: "#334155",
                          marginBottom: "2px",
                        }}
                      >
                        <span>Capacidad:</span>
                        <span style={{ fontWeight: "700", color: C.secondary }}>
                          {selectedSpace.cantidad_personas}
                        </span>
                      </div>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          fontSize: ".75rem",
                          color: "#334155",
                        }}
                      >
                        <span>Total personas:</span>
                        <span
                          style={{
                            fontWeight: "700",
                            color:
                              1 + clientesAsociadosInfo.length >
                              selectedSpace.cantidad_personas
                                ? C.primary
                                : C.secondaryLight,
                          }}
                        >
                          {1 + clientesAsociadosInfo.length}
                        </span>
                      </div>
                      {1 + clientesAsociadosInfo.length >
                        selectedSpace.cantidad_personas && (
                        <p
                          style={{
                            fontSize: ".68rem",
                            color: C.primary,
                            marginTop: "4px",
                            fontWeight: "700",
                          }}
                        >
                          ⚠ Excede la capacidad
                        </p>
                      )}
                    </div>
                  )}
                </div>

                {/* Detalles reserva */}
                <div
                  style={{
                    background: C.canvas,
                    border: `1px solid ${C.line}`,
                    borderRadius: "12px",
                    padding: "12px",
                  }}
                >
                  <p
                    style={{
                      fontSize: ".68rem",
                      fontWeight: "800",
                      color: C.muted,
                      textTransform: "uppercase",
                      letterSpacing: ".06em",
                      marginBottom: "10px",
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                  >
                    <Package size={11} /> Detalles
                  </p>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "0",
                    }}
                  >
                    {[
                      ["Categoría", resolvedCategory?.nombre],
                      ["Servicio", selectedService?.nombre],
                      ["Plan", selectedPlan?.plan_nombre],
                      selectedService?.requiere_espacio && selectedSpace
                        ? ["Espacio", selectedSpace.espacio_nombre]
                        : null,
                      fechaInicio
                        ? [
                            "Desde",
                            `${fmtDateSpanish(fechaInicio)} ${horaInicio || ""}`,
                          ]
                        : null,
                      fechaFin
                        ? [
                            "Hasta",
                            `${fmtDateSpanish(fechaFin)} ${usaTemporizador ? "Salida al finalizar" : horaFin || ""}`,
                          ]
                        : null,
                    ]
                      .filter(Boolean)
                      .map(
                        (row, i, arr) =>
                          row && (
                            <div
                              key={row[0]}
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                padding: "7px 0",
                                borderBottom:
                                  i < arr.length - 1
                                    ? `1px solid ${C.line}`
                                    : "none",
                              }}
                            >
                              <span
                                style={{
                                  fontSize: ".74rem",
                                  color: "#94a3b8",
                                  fontWeight: "500",
                                }}
                              >
                                {row[0]}
                              </span>
                              <span
                                className="ncp-num"
                                style={{
                                  fontSize: ".78rem",
                                  color: "#334155",
                                  fontWeight: "700",
                                  textAlign: "right",
                                  maxWidth: "55%",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {row[1] || "—"}
                              </span>
                            </div>
                          ),
                      )}
                  </div>
                </div>

                {/* Resumen pago */}
                <div
                  style={{
                    background: "#fff",
                    border: `1px solid ${C.line}`,
                    borderRadius: "12px",
                    padding: "12px",
                    boxShadow: C.shadowSm,
                  }}
                >
                  <p
                    style={{
                      fontSize: ".68rem",
                      fontWeight: "800",
                      color: C.muted,
                      textTransform: "uppercase",
                      letterSpacing: ".06em",
                      marginBottom: "10px",
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                  >
                    <DollarSign size={11} /> Pago
                  </p>

                  {/* Tarjeta de Beneficio Domiciliado */}
                  {afiliacionCliente &&
                    Number(afiliacionCliente.horas_gratuitas_disponibles) >
                      0 && (
                      <div
                        style={{
                          background:
                            "linear-gradient(135deg, #f5f3ff, #ede9fe)",
                          border: "1.5px solid #c4b5fd",
                          borderRadius: "12px",
                          padding: "12px",
                          marginBottom: "12px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: "8px",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                          }}
                        >
                          <div
                            style={{
                              width: "32px",
                              height: "32px",
                              borderRadius: "10px",
                              background: "#7c3aed",
                              color: "#fff",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              flexShrink: 0,
                            }}
                          >
                            <Gift size={16} />
                          </div>
                          <div>
                            <div
                              style={{
                                fontSize: ".7rem",
                                fontWeight: "800",
                                color: "#5b21b6",
                                textTransform: "uppercase",
                              }}
                            >
                              Cliente Domiciliado (
                              {afiliacionCliente.empresa_nombre})
                            </div>
                            <div
                              style={{
                                fontSize: ".75rem",
                                color: "#4c1d95",
                                fontWeight: "600",
                              }}
                            >
                              <strong>
                                {afiliacionCliente.horas_gratuitas_disponibles}{" "}
                                hrs de cortesía
                              </strong>{" "}
                              este mes
                            </div>
                            {afiliacionCliente.ciclo_actual_info && (
                              <div
                                style={{ fontSize: ".68rem", color: "#6d28d9" }}
                              >
                                Vence:{" "}
                                {afiliacionCliente.ciclo_actual_info.fecha_fin}{" "}
                                (No acumulable)
                              </div>
                            )}
                          </div>
                        </div>

                        <label
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                            cursor: "pointer",
                            background: "#fff",
                            padding: "5px 10px",
                            borderRadius: "8px",
                            border: "1px solid #ddd6fe",
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={usarHorasDomiciliado}
                            onChange={(e) =>
                              setUsarHorasDomiciliado(e.target.checked)
                            }
                            style={{
                              accentColor: "#7c3aed",
                              cursor: "pointer",
                            }}
                          />
                          <span
                            style={{
                              fontSize: ".72rem",
                              fontWeight: "800",
                              color: "#5b21b6",
                            }}
                          >
                            {usarHorasDomiciliado
                              ? "Solicitar uso de horas"
                              : "Aplicar Horas"}
                          </span>
                        </label>
                      </div>
                    )}

                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "8px 0",
                      borderBottom: `1px solid ${C.line}`,
                      marginBottom: "10px",
                    }}
                  >
                    <span
                      style={{
                        fontSize: ".8rem",
                        color: C.muted,
                        fontWeight: "500",
                      }}
                    >
                      Subtotal
                    </span>
                    <span
                      className="ncp-num"
                      style={{
                        fontSize: "1rem",
                        fontWeight: "900",
                        color: C.ink,
                      }}
                    >
                      {fmtMonto(subtotalNeto)} Bs.
                    </span>
                  </div>

                  <label htmlFor="ncp-descuento-servicio" className="block rounded-xl border border-sky-100 p-3 mb-2 text-xs font-semibold">
                    Descuento de línea del servicio (Bs.)
                    <input id="ncp-descuento-servicio" type="number" min="0" max={subtotalServicio} step="0.01"
                      className="ncp-input w-full mt-2" disabled={usaTemporizador || loading}
                      value={usaTemporizador ? '' : descuentoServicio === '' ? '' : descuentoServicioNum} placeholder="0.00"
                      onChange={e => {const v=e.target.value;const n=Number(v);if(v==='')setDescuentoServicio('');else if(Number.isFinite(n))setDescuentoServicio(Math.max(0,Math.min(n,subtotalServicio)));}} />
                    <span className="block mt-1 font-normal">Subtotal del servicio: Bs {(subtotalServicio - descuentoServicioNum).toFixed(2)}</span>
                  </label>
                  {/* Descuento global */}
                  <div
                    style={{
                      background: C.canvas,
                      borderRadius: "10px",
                      padding: "10px 12px",
                      marginBottom: "8px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <label
                        htmlFor="ncp-descuento"
                        style={{
                          fontSize: ".75rem",
                          fontWeight: "700",
                          color: "#334155",
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                        }}
                      >
                        <Percent size={11} color={C.secondary} /> Descuento global
                      </label>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                        }}
                      >
                        <input
                          id="ncp-descuento"
                          type="number"
                          min={0}
                          max={subtotalNeto}
                          step={0.01}
                          value={usaTemporizador ? "" : descuentoDirecto === '' ? '' : descuentoGlobal}
                          placeholder="0.00"
                          disabled={usaTemporizador}
                          onChange={(e) => {
                            if (usaTemporizador) return;
                            const v = e.target.value;
                            if (v === "") {
                              setDescuentoDirecto("");
                              return;
                            }
                            const n = Number(v);
                            if (
                              !isNaN(n) &&
                              n >= 0 &&
                              n <= subtotalNeto
                            )
                              setDescuentoDirecto(n);
                          }}
                          className="ncp-input ncp-num"
                          style={{
                            width: "84px",
                            textAlign: "right",
                            padding: "4px 6px",
                            minHeight: "34px",
                            borderRadius: "7px",
                            border: `1px solid ${C.line}`,
                            background: usaTemporizador ? "#f1f5f9" : "#fff",
                            fontSize: ".85rem",
                            color: usaTemporizador ? "#94a3b8" : "#334155",
                            cursor: usaTemporizador ? "not-allowed" : "auto",
                          }}
                        />
                        <span
                          style={{
                            fontSize: ".72rem",
                            color: "#94a3b8",
                            fontWeight: "600",
                          }}
                        >
                          Bs.
                        </span>
                      </div>
                    </div>
                    {usaTemporizador && (
                      <p
                        style={{
                          fontSize: ".68rem",
                          color: C.accentDark,
                          marginTop: "6px",
                          display: "flex",
                          alignItems: "center",
                          gap: "3px",
                        }}
                      >
                        <Timer size={10} />
                        El descuento se aplica al finalizar el temporizador.
                      </p>
                    )}
                  </div>

                  {(onReservaCreada || selectedService?.tipo_servicio === 'oficina_virtual') && (
                    <section className="rounded-xl border border-sky-100 bg-sky-50/50 p-3 mb-3">
                      <button
                        type="button"
                        className="rw-button w-full"
                        disabled={evaluandoReglas}
                        onClick={evaluarCondiciones}
                      >
                        {evaluandoReglas
                          ? "Validando…"
                          : selectedService?.tipo_servicio === 'oficina_virtual'
                            ? "Actualizar cotización" : "Validar condiciones del plan"}
                      </button>
                      {errorReglas && (
                        <p role="alert" className="mt-2 text-xs text-rose-700">
                          {errorReglas}
                        </p>
                      )}
                      {reglasEvaluadas && (
                        <>
                          <p className="mt-2 text-xs text-emerald-700">
                            {reglasEvaluadas.mensaje}
                          </p>
                          {reglasEvaluadas.consumo && (
                            <div className="mt-2 text-xs space-y-1">
                              <p>
                                Solicitado: {reglasEvaluadas.consumo.horas} h
                              </p>
                              <p>
                                Incluido: {reglasEvaluadas.consumo.incluidas} h
                              </p>
                              <p className="text-amber-800">
                                Excedente: {reglasEvaluadas.consumo.excedentes}{" "}
                                h · Bs{" "}
                                {reglasEvaluadas.consumo.precio_excedente}
                              </p>
                            </div>
                          )}
                          <p className="mt-2 text-sm font-bold">
                            Precio sugerido: Bs {precioSugerido.toFixed(2)}
                          </p>
                          {reglasEvaluadas.puede_ajustar_precio &&
                            !usaTemporizador && (
                              <>
                                <label className="block mt-3 text-xs font-semibold">
                                  Precio final autorizado
                                  <input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    className="ncp-input w-full mt-1"
                                    value={precioAutorizado}
                                    placeholder={precioSugerido.toFixed(2)}
                                    onChange={(e) =>
                                      setPrecioAutorizado(e.target.value)
                                    }
                                  />
                                </label>
                                {precioAutorizado !== "" && (
                                  <label className="block mt-2 text-xs text-amber-800">
                                    Precio modificado · motivo obligatorio
                                    <textarea
                                      className="ncp-input w-full mt-1"
                                      maxLength={500}
                                      value={motivoPrecio}
                                      onChange={(e) =>
                                        setMotivoPrecio(e.target.value)
                                      }
                                      placeholder="Motivo de la autorización"
                                    />
                                  </label>
                                )}
                              </>
                            )}
                        </>
                      )}
                    </section>
                  )}
                  {/* Cupón */}
                  <div
                    style={{
                      background: C.canvas,
                      borderRadius: "10px",
                      padding: "10px 12px",
                      marginBottom: "8px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        marginBottom: "6px",
                      }}
                    >
                      <label
                        htmlFor="ncp-cupon"
                        style={{
                          fontSize: ".75rem",
                          fontWeight: "700",
                          color: "#334155",
                        }}
                      >
                        Cupón
                      </label>
                      <button
                        type="button"
                        onClick={validarCupon}
                        style={{
                          padding: "3px 10px",
                          minHeight: 30,
                          borderRadius: "6px",
                          background: `linear-gradient(135deg,${C.secondary},${C.secondaryDark})`,
                          color: "#fff",
                          border: "none",
                          fontSize: ".68rem",
                          fontWeight: "700",
                          cursor: "pointer",
                        }}
                      >
                        Aplicar
                      </button>
                    </div>
                    <input
                      id="ncp-cupon"
                      type="text"
                      value={cuponCodigo}
                      onChange={(e) => setCuponCodigo(e.target.value)}
                      onBlur={validarCupon}
                      placeholder="Código del cupón"
                      className="ncp-input"
                      style={{
                        width: "100%",
                        padding: "7px 10px",
                        minHeight: "36px",
                        borderRadius: "8px",
                        border: `1px solid ${C.line}`,
                        background: "#fff",
                        fontSize: ".8rem",
                        color: "#334155",
                      }}
                    />
                  </div>

                  {cuponAplicado && (
                    <div
                      style={{
                        padding: "8px 10px",
                        background: `${C.secondaryLight}0a`,
                        border: `1px solid ${C.secondaryLight}40`,
                        borderRadius: "8px",
                        marginBottom: "8px",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <div>
                        <p
                          style={{
                            fontSize: ".75rem",
                            fontWeight: "700",
                            color: "#065f46",
                          }}
                        >
                          ✓ Cupón aplicado
                        </p>
                        <p
                          className="ncp-num"
                          style={{ fontSize: ".68rem", color: C.muted }}
                        >
                          {cuponAplicado.codigo} ·{" "}
                          {cuponAplicado.descuento_monto
                            ? `${cuponAplicado.descuento_monto} Bs.`
                            : `${cuponAplicado.descuento_porcentaje}%`}
                        </p>
                      </div>
                      <button
                        type="button"
                        aria-label="Quitar cupón"
                        onClick={() => {
                          setCuponAplicado(null);
                          setCuponCodigo("");
                        }}
                        style={{
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          color: C.primary,
                          padding: "6px",
                          borderRadius: "4px",
                        }}
                      >
                        <X size={13} />
                      </button>
                    </div>
                  )}

                  {posMixto && sucursalId && <ProductosReservaSelector
                    sucursalId={sucursalId} value={productosReserva} onChange={setProductosReserva} disabled={loading} />}
                  {posMixto && totalProductos > 0 && <div className="text-sm mb-2 space-y-1">
                    <p>Servicio: Bs {(totalFinal - totalProductos).toFixed(2)}</p>
                    <p>Productos: Bs {totalProductos.toFixed(2)}</p>
                  </div>}
                  {/* Total final */}
                  <div
                    style={{
                      paddingTop: "10px",
                      borderTop: `2px solid ${C.line}`,
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <span
                      style={{
                        fontSize: ".9rem",
                        fontWeight: "800",
                        color: C.navy,
                      }}
                    >
                      Total
                    </span>
                    <span
                      className="ncp-num"
                      style={{
                        fontSize: "1.35rem",
                        fontWeight: "900",
                        color: C.secondaryDark,
                      }}
                    >
                      {totalFinal.toFixed(2)} Bs.
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Botón confirmar */}
          <div
            style={{
              padding: "12px",
              borderTop: `1px solid ${C.line}`,
              flexShrink: 0,
            }}
          >
            <button
              type="button"
              className={`ncp-confirm-btn ${isReadyToConfirm ? "ready" : "disabled"}`}
              onClick={() => handleCreateReservation("guardar")}
              disabled={!isReadyToConfirm}
            >
              {loading ? (
                <>
                  <span
                    style={{
                      width: "16px",
                      height: "16px",
                      border: "2px solid rgba(255,255,255,.4)",
                      borderTopColor: "#fff",
                      borderRadius: "50%",
                      animation: "ncpSpin .7s linear infinite",
                      display: "inline-block",
                    }}
                  />{" "}
                  Procesando...
                </>
              ) : (
                <>
                  <Zap size={16} />
                  {onReservaCreada ? "Guardar reserva" : "Confirmar Reserva"}
                </>
              )}
            </button>
            {onReservaCreada && (
              <div style={{ display: "grid", gap: 8, marginTop: 8 }}>
                <button
                  type="button"
                  className="rw-button"
                  disabled={!isReadyToConfirm}
                  onClick={() => handleCreateReservation("despues")}
                >
                  Guardar y pagar después
                </button>
                <button
                  type="button"
                  className="rw-button rw-primary"
                  disabled={!isReadyToConfirm}
                  onClick={() => handleCreateReservation("cobrar")}
                >
                  <CreditCard size={16} />
                  Guardar y cobrar en POS
                </button>
              </div>
            )}
            {!isReadyToConfirm && (
              <p
                style={{
                  textAlign: "center",
                  fontSize: ".68rem",
                  color: "#94a3b8",
                  marginTop: "6px",
                  fontWeight: "500",
                }}
              >
                {!clienteId
                  ? "Selecciona un cliente"
                  : !selectedService
                    ? "Selecciona un servicio"
                    : !resolvedCategory
                      ? "No se pudo determinar la categoría del servicio"
                      : !selectedPlan
                        ? "Selecciona un plan"
                        : selectedService?.requiere_espacio && !selectedSpace
                          ? "Selecciona un espacio"
                          : selectedService?.requiere_espacio &&
                              selectedSpace &&
                              espaciosDisponibles[selectedSpace.id] !== true
                            ? "Verificando disponibilidad del espacio"
                            : operadorRequerido &&
                                fechaInicio &&
                                fechaFin &&
                                horaInicio &&
                                (usaTemporizador || horaFin)
                              ? "Identifica al operador de caja con su PIN (botón Operador del navbar)"
                              : duracionVirtualInvalida
                                ? "Configura los días y las horas del plan, sin temporizador"
                                : selectedService?.tipo_servicio === 'oficina_virtual' && errorReglas
                                  ? errorReglas
                                  : selectedService?.tipo_servicio === 'oficina_virtual' && evaluandoReglas
                                    ? "Consultando la tarifa del servicio…"
                                    : "Completa las fechas, horas y períodos"}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* ── Modales ── */}
      {showClientModal && (
        <ClientePOSModal
          key={`${puntoDeVenta.id}:${clienteEditar?.id || "nuevo"}`}
          cliente={clienteEditar || undefined}
          posId={puntoDeVenta.id}
          onClose={handleCloseClientModal}
          onSaved={handleClienteGuardado}
        />
      )}
      <ModalAgregarClientesAsociados
        isOpen={showAgregarClientesModal}
        onClose={() => setShowAgregarClientesModal(false)}
        onAgregar={handleAgregarClientesAsociados}
        clientesExcluir={
          [clienteId, ...clientesAsociadosInfo.map(c => c.id)].filter(Boolean) as number[]
        }
        clientes={clientes}
        capacidadMaxima={
          selectedSpace
            ? Math.max(0, selectedSpace.cantidad_personas - 1 - clientesAsociados.length)
            : undefined
        }
      />

      <Modal isOpen={showSuccessModal} onClose={handleModalClose} size="md">
        <div
          style={{
            textAlign: "center",
            padding: "32px 24px",
            fontFamily: "'DM Sans',sans-serif",
          }}
        >
          <div
            style={{
              width: "64px",
              height: "64px",
              borderRadius: "50%",
              background: `${C.secondaryLight}18`,
              border: `2px solid ${C.secondaryLight}30`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 16px",
            }}
          >
            <CheckCircle size={28} color={C.secondaryLight} />
          </div>
          <h3
            style={{
              fontSize: "1.2rem",
              fontWeight: "900",
              color: C.navy,
              marginBottom: "6px",
            }}
          >
            ¡Reserva creada!
          </h3>
          <div
            style={{
              display: "flex",
              gap: "4px",
              justifyContent: "center",
              marginBottom: "12px",
            }}
          >
            {[C.primary, C.secondary, C.accentDark, C.secondaryLight].map(
              (c, i) => (
                <div
                  key={i}
                  style={{
                    height: "2px",
                    width: i === 0 ? "18px" : "6px",
                    borderRadius: "2px",
                    background: c,
                  }}
                />
              ),
            )}
          </div>
          <p
            style={{ fontSize: ".88rem", color: C.muted, marginBottom: "20px" }}
          >
            La reserva ha sido registrada correctamente.
          </p>
          <div
            style={{
              display: "flex",
              gap: "10px",
              justifyContent: "center",
              flexWrap: "wrap",
            }}
          >
            <button
              onClick={() => {
                handleModalClose();
                navigate("/reservas-calendario");
              }}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                padding: "10px 22px",
                borderRadius: "11px",
                border: `1.5px solid ${C.secondary}35`,
                background: `${C.secondary}08`,
                color: C.secondary,
                fontWeight: "800",
                fontSize: ".88rem",
                cursor: "pointer",
                transition: "all .2s",
              }}
            >
              <Calendar size={16} /> Volver al Calendario
            </button>
            <button
              onClick={handleModalClose}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                padding: "10px 24px",
                borderRadius: "11px",
                border: "none",
                background: `linear-gradient(135deg,${C.secondary},${C.secondaryDark})`,
                color: "#fff",
                fontWeight: "800",
                fontSize: ".88rem",
                cursor: "pointer",
                boxShadow: `0 4px 14px ${C.secondary}30`,
                transition: "all .2s",
              }}
            >
              <Plus size={16} /> Nueva reserva
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={showAvailabilityModal}
        onClose={() => setShowAvailabilityModal(false)}
        size="md"
      >
        <div
          style={{
            textAlign: "center",
            padding: "32px 24px",
            fontFamily: "'DM Sans',sans-serif",
          }}
        >
          <div
            style={{
              width: "64px",
              height: "64px",
              borderRadius: "50%",
              background: `${C.primary}10`,
              border: `2px solid ${C.primary}22`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 16px",
            }}
          >
            <X size={28} color={C.primary} />
          </div>
          <h3
            style={{
              fontSize: "1.2rem",
              fontWeight: "900",
              color: C.navy,
              marginBottom: "6px",
            }}
          >
            Espacio no disponible
          </h3>
          <div
            style={{
              display: "flex",
              gap: "4px",
              justifyContent: "center",
              marginBottom: "12px",
            }}
          >
            {[C.primary, C.secondary, C.accentDark, C.secondaryLight].map(
              (c, i) => (
                <div
                  key={i}
                  style={{
                    height: "2px",
                    width: i === 0 ? "18px" : "6px",
                    borderRadius: "2px",
                    background: c,
                    opacity: 0.4,
                  }}
                />
              ),
            )}
          </div>
          <p
            style={{ fontSize: ".88rem", color: C.muted, marginBottom: "20px" }}
          >
            El espacio no está disponible en el horario seleccionado. Elige otro
            horario o espacio.
          </p>
          <button
            type="button"
            onClick={() => setShowAvailabilityModal(false)}
            style={{
              padding: "10px 28px",
              borderRadius: "11px",
              border: `1.5px solid ${C.secondary}25`,
              background: `${C.secondary}08`,
              color: C.secondary,
              fontWeight: "800",
              fontSize: ".88rem",
              cursor: "pointer",
            }}
          >
            Entendido
          </button>
        </div>
      </Modal>
    </>
  );
};

export default NuevaCitaContent;

